import * as THREE from 'three';
import {
  IndirectStorageBufferAttribute,
  StorageBufferAttribute,
} from 'three/webgpu';
import { storage, uniform, workgroupId, localId, wgsl, wgslFn } from './tslCompat';
import { assertStorageBufferBudget } from './bindingBudget';
import {
  CONTACT_RECORD_META_OFFSET,
  CONTACT_RECORD_NORMAL_PEN_OFFSET,
  CONTACT_RECORD_VEC4S,
} from './contactRecord';
import {
  AVBD_ADAPTIVE_SLEEP_CONVERGED,
  AVBD_ADAPTIVE_SLEEP_POLISH_COMPLETED,
  AVBD_ADAPTIVE_SLEEP_POLISH_CONFIGURED,
  AVBD_ADAPTIVE_SLEEP_POLISH_REQUESTED,
  AVBD_ADAPTIVE_SLEEP_POLISH_STEPS,
  AVBD_ADAPTIVE_STATE_WORDS,
  BODY_SLEEP_FLAG,
} from './avbdState';
import {
  SOLVER_CONTACT_CANONICAL_ALIAS_BIT,
} from './solverContactList';
import {
  createGpuOnlyStorageAttribute,
  type GpuOnlyStorageAllocation,
} from './gpuOnlyStorage';

const WORKGROUP_SIZE = 256;
// A single invocation is faster and exact for tiny contact graphs. It avoids
// paying dozens of dispatches for the parallel convergence protocol in scenes
// such as the 27-body sleeping regression.
// A single ordered invocation is materially faster than encoding the bounded
// parallel convergence train for small/medium contact graphs. 1,024 covers
// the production 10x10x10 stack (plus floor) while keeping large 50K/64K
// scenes on the workgroup-parallel path.
const SERIAL_DSU_BODY_THRESHOLD = 1024;
const DSU_CONTINUATION_CLEAR_BINDINGS = 10;
const INERTIAL_POSE_VEC4S_PER_BODY = 5;
const BODY_HISTORY_VEC4S = 4;
const ISLAND_WORDS = 4;
const TELEMETRY_WORDS = 48;

// Persistent, GPU-owned sleeping gate. CPU telemetry is deliberately not part
// of the control loop: one animation frame can contain several real physics
// substeps, and waiting for a readback would make wake/sleep decisions stale.
const GATE_CONTROL_WORDS = 11;
const GATE_MODE = 0;
const GATE_SUBSTEP_EPOCH = 1;
const GATE_SLEEPING_BODIES = 2;
const GATE_CANDIDATE_READY = 3;
const GATE_MAX_CANDIDATE = 4;
const GATE_PATH = 5;
const GATE_AWAKE_FAST_SUBSTEPS = 6;
const GATE_FULL_DSU_SUBSTEPS = 7;
const GATE_CANDIDATE_FALLBACKS = 8;
const GATE_CACHED_ROOT_HITS = 9;
const GATE_CACHED_ROOT_MISSES = 10;

const GATE_MODE_AWAKE_FAST = 0;
const GATE_MODE_FULL_VALIDATE = 1;
const GATE_PATH_NONE = 0;
const GATE_PATH_AWAKE_FAST = 1;
const GATE_PATH_FULL_VALIDATE = 2;
const GATE_PATH_CACHE_VALIDATE = 3;
const GATE_PATH_CACHED_ROOT = 4;

// Published topology cache. VALID is written last, after every body root and
// the exact set of canonical dynamic-dynamic body pairs has been staged and
// checked. Physical contact rows may relocate, but pair insertion/membership
// plus an exact unique-pair count prove that the connected-component partition
// is unchanged. A miss clears VALID before scheduling full DSU, so a failed
// rebuild can never make partially overwritten cache data visible on the next
// substep.
const CACHE_HEADER_WORDS = 8;
const CACHE_VALID = 0;
const CACHE_VERSION = 1;
const CACHE_BODY_COUNT = 2;
const CACHE_TOPOLOGY_GENERATION = 3;
const CACHE_DYNAMIC_CONTACTS = 4;
const CACHE_PUBLISHED_EPOCH = 5;
const CACHE_DISABLED = 6;

const CACHE_VALIDATION_WORDS = 5;
const CACHE_VALIDATION_FAILURES = 0;
const CACHE_VALIDATION_DYNAMIC_CONTACTS = 1;
const CACHE_STAGED_DYNAMIC_CONTACTS = 2;
const CACHE_STAGED_VERSION = 3;
const CACHE_STAGING_FAILURES = 4;

const CACHE_FAIL_HEADER = 0x00000001;
const CACHE_FAIL_BODY_CLASS = 0x00000002;
const CACHE_FAIL_ROOT = 0x00000004;
const CACHE_FAIL_CONTACT_ROW = 0x00000010;
const CACHE_FAIL_CONTACT_PAIR = 0x00000020;
const CACHE_FAIL_DUPLICATE_ROW = 0x00000040;
const CACHE_FAIL_CONTACT_COUNT = 0x00000080;

// Pair tables run at <= 50% planned load. A bounded probe failure merely
// declines cache publication (or produces a cache miss); it can never accept
// an inexact topology. This keeps pathological hashes from stalling a frame.
const CACHE_PAIR_MAX_PROBES = 128;
const CACHE_STATIC_ROOT = 0xffffffff;

const BODY_INITIALIZED = 0x00000001;
export const SLEEPING_BODY_STATE_SLEEPING_BIT = 0x00000002;
const BODY_LOCAL_WAKE = 0x00000004;
const BODY_LOCAL_FAULT = 0x00000008;
const BODY_STATIC_MOVED = 0x00000010;
const BODY_CONSERVATIVE = 0x00000020;

const ISLAND_SUPPORTED = 0x00000001;
const ISLAND_INELIGIBLE = 0x00000002;
const ISLAND_WAKE = 0x00000004;
const ISLAND_FAULT = 0x00000008;
const ISLAND_HAS_SLEEPING = 0x00000010;
const ISLAND_HAS_AWAKE = 0x00000020;
// Contact geometry is generated before sleeping. Preserve this bit and the
// support bit across the after-solve aggregate clear. The canonical active
// list stays intact when solver-list scratch compaction is available.
const ISLAND_CONTACT_INELIGIBLE = 0x00000040;
// Transient post-solve admission block. Unlike physical ineligibility, this
// bit is ignored when deciding whether a quiet island should request polish.
const ISLAND_ADAPTIVE_BLOCK = 0x00000080;

const FRAME_FAIL_OPEN = 0x00000001;
const FRAME_INCOMPATIBLE = 0x00000002;
const FRAME_ACTIVE_LIST_OVERFLOW = 0x00000004;
const FRAME_UNION_INCOMPLETE = 0x00000008;
const FRAME_INVALID_INPUT = 0x00000010;
const FRAME_FORCED_WAKE = 0x00000020;
const FRAME_RESET = 0x00000040;
const FRAME_SOLVER_LIST_OVERFLOW = 0x00000080;
const FRAME_FAIL_MASK = FRAME_FAIL_OPEN
  | FRAME_ACTIVE_LIST_OVERFLOW
  | FRAME_UNION_INCOMPLETE
  | FRAME_INVALID_INPUT
  | FRAME_SOLVER_LIST_OVERFLOW;

const TELEMETRY_FRAME = 0;
const TELEMETRY_FLAGS = 1;
const TELEMETRY_ACTIVE_CONTACTS = 2;
const TELEMETRY_DYNAMIC_BODIES = 3;
const TELEMETRY_ISLANDS = 4;
const TELEMETRY_SUPPORTED_ISLANDS = 5;
const TELEMETRY_ELIGIBLE_ISLANDS = 6;
const TELEMETRY_SLEEPING_BODIES = 7;
const TELEMETRY_SLEEPING_ISLANDS = 8;
const TELEMETRY_WAKE_EVENTS = 9;
const TELEMETRY_SLEEP_EVENTS = 10;
const TELEMETRY_INVALID_INPUTS = 11;
const TELEMETRY_UNION_FAILURES = 12;
const TELEMETRY_MAX_ISLAND_SIZE = 13;
const TELEMETRY_MAX_LINEAR_SPEED_BITS = 14;
const TELEMETRY_MAX_ANGULAR_SPEED_BITS = 15;
const TELEMETRY_INELIGIBLE_MOTION_BODIES = 16;
const TELEMETRY_INELIGIBLE_CORRECTION_BODIES = 17;
const TELEMETRY_INELIGIBLE_CONTACTS = 18;
const TELEMETRY_MAX_CONTACT_PENETRATION_BITS = 19;
const TELEMETRY_FAST_GATE = 20;
const TELEMETRY_MAX_ISLAND_DWELL = 21;
const TELEMETRY_SOLVER_CONTACTS = 22;
const TELEMETRY_SOLVER_LIST_OVERFLOWS = 23;
const TELEMETRY_PRE_SOLVE_SLEEPING_BODIES = 24;
const TELEMETRY_SOLVER_CANONICAL_ALIAS = 25;
const TELEMETRY_GATE_MODE = 26;
const TELEMETRY_SUBSTEP_EPOCH = 27;
const TELEMETRY_AWAKE_FAST_GATED = 28;
const TELEMETRY_FULL_DSU_THIS_SUBSTEP = 29;
const TELEMETRY_MAX_CANDIDATE_FRAMES = 30;
const TELEMETRY_AWAKE_FAST_SUBSTEPS = 31;
const TELEMETRY_FULL_DSU_SUBSTEPS = 32;
const TELEMETRY_CANDIDATE_FALLBACKS = 33;
const TELEMETRY_CACHED_ROOT_HIT = 34;
const TELEMETRY_CACHED_ROOT_MISS = 35;
const TELEMETRY_CACHED_ROOT_HITS = 36;
const TELEMETRY_CACHED_ROOT_MISSES = 37;
const TELEMETRY_CACHE_VALIDATION_FAILURES = 38;
const TELEMETRY_TOPOLOGY_PATH = 39;
const TELEMETRY_CACHED_DYNAMIC_CONTACTS = 40;
const TELEMETRY_APPEND_PRESERVED = 41;
const TELEMETRY_COMPACT_VALID_CONTACTS = 42;
const TELEMETRY_COMPACT_SLEEPING_FILTERED = 43;
const TELEMETRY_COMPACT_INVALID_RETAINED = 44;
// Written once by the planner dispatch that is queue-ordered after the direct
// begin-frame body dispatch. A queue-drained telemetry read may therefore
// certify the GPU buffer itself rather than infer publication from a CPU-side
// command-submission callback.
const TELEMETRY_INITIALIZED_BODIES = 45;
// Exact GPU-side controller accounting. A full-DSU frame initializes the
// saved count with the removed per-round clear dispatches minus the bounded
// continuation cleanup groups; the first converged finalizer adds every
// now-skipped trailing finalizer. This is a proof of actual non-zero indirect
// dispatches removed, not a CPU estimate.
const TELEMETRY_DSU_ROUNDS_EXECUTED = 46;
const TELEMETRY_DSU_DISPATCHES_SAVED = 47;

export type SleepingIslandsOptions = {
  gravity?: [number, number, number];
  sleepFrames?: number;
  sleepLinearSpeed?: number;
  sleepAngularSpeed?: number;
  wakeLinearSpeed?: number;
  wakeAngularSpeed?: number;
  wakePositionDelta?: number;
  wakeAngularDelta?: number;
  sleepPositionCorrection?: number;
  sleepAngularCorrection?: number;
  sleepContactPenetration?: number;
  wakeContactPenetration?: number;
  supportNormalCosine?: number;
  unionPasses?: number;
};

export type SleepingIslandsStageInputs = {
  renderer: any;
  device: GPUDevice;
  positions: StorageBufferAttribute;
  quaternions: StorageBufferAttribute;
  velocities: StorageBufferAttribute;
  angularVelocities: StorageBufferAttribute;
  shapes: StorageBufferAttribute;
  initialPose: StorageBufferAttribute;
  inertialPose: StorageBufferAttribute;
  /** Queue-ordered AVBD primal/dual/penalty convergence certificate. */
  adaptiveSleepState: StorageBufferAttribute;
  conservativeBodyFlags: StorageBufferAttribute;
  bodyConstraintCounts: StorageBufferAttribute;
  pairContacts: StorageBufferAttribute;
  pairActivity: StorageBufferAttribute;
  pairBodyContactCounts: StorageBufferAttribute;
  pairBodyContactIndices: StorageBufferAttribute;
  /**
   * Optional caller-owned body state shared with an earlier broadphase stage.
   * Sharing avoids a copy and lets broadphase conservatively retain the last
   * exact AABB for bodies covered by a current sleeping certificate.
   */
  bodyState?: StorageBufferAttribute;
  maxBodies: number;
  maxPairContacts: number;
  maxActivePairContacts: number;
  /** Upper bound on simultaneously live canonical body pairs. */
  maxCachedPairs: number;
  maxPairContactsPerBody: number;
  pairActiveContactsOffset: number;
  /**
   * Optional post-sleep solver list. The pairActiveCandidateSlots segment is
   * dead after narrowphase, so the engine can reuse it without allocating a
   * second O(contact-capacity) buffer. Compaction is enabled only when this
   * segment can hold every entry from the canonical active list.
   */
  pairSolverContactsOffset?: number;
  pairSolverContactsCapacity?: number;
  options?: SleepingIslandsOptions;
};

export type SleepingIslandsFrameParams = {
  bodyCount: number;
  /** Exact CPU-owned count of finite positive-inverse-mass bodies. */
  dynamicBodyCount: number;
  frameId: number;
  jointCount?: number;
  springCount?: number;
  restitutionActive?: boolean;
  forceWake?: boolean;
  topologyGeneration?: number;
  /** Exact f32 inertial field included in the CPU zero-work certificate. */
  gravity?: readonly [number, number, number];
};

export type SleepingIslandsTelemetry = {
  frameId: number;
  flags: number;
  failOpen: boolean;
  incompatible: boolean;
  activeContacts: number;
  dynamicBodies: number;
  islands: number;
  supportedIslands: number;
  eligibleIslands: number;
  sleepingBodies: number;
  sleepingIslands: number;
  wakeEvents: number;
  sleepEvents: number;
  invalidInputs: number;
  unionFailures: number;
  maxIslandSize: number;
  maxLinearSpeed: number;
  maxAngularSpeed: number;
  ineligibleMotionBodies: number;
  ineligibleCorrectionBodies: number;
  ineligibleContacts: number;
  maxContactPenetration: number;
  fastGated: boolean;
  maxIslandDwell: number;
  solverContacts: number;
  solverListOverflows: number;
  solverContactCompaction: boolean;
  preSolveSleepingBodies: number;
  solverCanonicalAlias: boolean;
  gateMode: 'awake-fast' | 'full-validate';
  substepEpoch: number;
  awakeFastGated: boolean;
  fullDsuThisSubstep: boolean;
  maxCandidateFrames: number;
  awakeFastSubsteps: number;
  fullDsuSubsteps: number;
  candidateFallbacks: number;
  cachedRootHit: boolean;
  cachedRootMiss: boolean;
  cachedRootHits: number;
  cachedRootMisses: number;
  cacheValidationFailures: number;
  topologyPath: 'none' | 'awake-fast' | 'cached-root' | 'full-dsu';
  cachedDynamicContacts: number;
  appendPreserved: boolean;
  compactValidContacts: number;
  compactSleepingFiltered: number;
  compactInvalidRetained: number;
  initializedBodies: number;
  dsuRoundsExecuted: number;
  dsuDispatchesSaved: number;
};

export type SleepingTopologyCacheAllocationTelemetry = {
  gpuOnly: true;
  totalCpuBytes: number;
  totalGpuBytes: number;
  cachedBodyRootBytes: number;
  /** Row-independent canonical body-pair hash table. */
  cachedPairKeyBytes: number;
  rowSeenBitsetBytes: number;
  pairSeenBitsetBytes: number;
  seenBitsetBytes: number;
  destroyed: boolean;
};

export type SleepingZeroWorkDiagnostics = Readonly<{
  eligible: boolean;
  confirmedFrameId: number;
  confirmedBodyCount: number;
  confirmedTopologyGeneration: number | null;
  mutationEpoch: number;
  completedMutationEpoch: number;
  completedFrameId: number;
  completedFrameSubmitted: boolean;
  confirmations: number;
  skippedFixedSteps: number;
  invalidations: number;
}>;

/**
 * CPU-side permission for the broadphase to consume the previous submitted
 * GPU sleeping state as a sparse query mask. This is deliberately weaker than
 * the all-sleeping zero-work certificate: mixed awake/sleeping worlds may use
 * it, but only after the complete sleeping/contact transaction for the same
 * mutation and topology generation was accepted by the queue.
 */
export type SleepingSparseBroadphaseCertificate = Readonly<{
  eligible: boolean;
  generation: number;
  confirmedFrameId: number;
  confirmedBodyCount: number;
  confirmedTopologyGeneration: number | null;
  confirmedMutationEpoch: number;
}>;

export type SleepingComputeGroupDiagnostics = Readonly<{
  beforeSolveGroups: number;
  afterSolveGroups: number;
  beforeSolveNodes: number;
  afterSolveNodes: number;
  dsuRoundBudget: number;
  dsuCleanupDispatches: number;
}>;

/**
 * Conservative contact-only GPU sleeping.
 *
 * beforeSolve() must be recorded after contact generation and before AVBD
 * prepare. afterSolve() must be recorded after AVBD velocity finalization.
 * Neither method owns a command encoder or calls queue.submit. The builders
 * retain a flat compatibility view and emit one ordered compute group per
 * phase. WebGPU gives every compute dispatch its own usage scope, so a storage
 * producer and a later storage/indirect consumer are valid in the same pass as
 * long as one dispatch does not bind the same buffer incompatibly.
 */
export class SleepingIslandsStage {
  private readonly maxBodies: number;
  private readonly maxPairContacts: number;
  private readonly maxActivePairContacts: number;
  private readonly cachePairHashCapacity: number;
  private readonly pairActiveContactsOffset: number;
  private readonly pairSolverContactsOffset: number;
  private readonly pairSolverContactsCapacity: number;
  private readonly solverContactCompactionEnabled: boolean;
  private readonly sleepFrames: number;
  private readonly unionPasses: number;
  private readonly maxRootDepth: number;

  private readonly bodyStateAttr: StorageBufferAttribute;
  private readonly stableFramesAttr: StorageBufferAttribute;
  private readonly candidateFramesAttr: StorageBufferAttribute;
  private readonly gateControlAttr: StorageBufferAttribute;
  private readonly parentAttr: StorageBufferAttribute;
  private readonly unionChangedAttr: StorageBufferAttribute;
  private readonly islandDataAttr: StorageBufferAttribute;
  private readonly poseHistoryAttr: StorageBufferAttribute;
  private readonly telemetryAttr: StorageBufferAttribute;
  private readonly cacheHeaderAttr: StorageBufferAttribute;
  private readonly cacheValidationAttr: StorageBufferAttribute;
  private readonly cachedBodyRootAttr: StorageBufferAttribute;
  private readonly cachedPairKeyAttr: StorageBufferAttribute;
  private readonly cacheSeenBitsAttr: StorageBufferAttribute;
  private readonly cachePairSeenBitsAttr: StorageBufferAttribute;
  private readonly cachedBodyRootAllocation: GpuOnlyStorageAllocation<Uint32Array>;
  private readonly cachedPairKeyAllocation: GpuOnlyStorageAllocation<Uint32Array>;
  private readonly cacheSeenBitsAllocation: GpuOnlyStorageAllocation<Uint32Array>;
  private readonly cachePairSeenBitsAllocation: GpuOnlyStorageAllocation<Uint32Array>;
  private readonly contactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly fullDsuContactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly fullDsuBodyDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly cacheValidationContactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly cacheValidationBodyDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly cacheSeenClearDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly cacheStagingSeenClearDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly unionContactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly unionBodyDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly stageBodyDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly fastCleanupDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly serialDsuDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly unionRoundDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly unionRoundDispatchIndirectAttrs: readonly IndirectStorageBufferAttribute[];
  private readonly solverContactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly candidateUpdateDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly candidateResetBeforeDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly candidateResetAfterDispatchIndirectAttr: IndirectStorageBufferAttribute;

  private readonly clearTelemetryKernel: any;
  private readonly initializeDsuKernel: any;
  private readonly beginFrameKernel: any;
  private readonly planPersistentGateKernel: any;
  private readonly buildContactDispatchKernel: any;
  private readonly planTopologyDispatchKernel: any;
  private readonly validateCachedBodiesKernel: any;
  private readonly validateCachedContactsKernel: any;
  private readonly clearCacheSeenBitsKernel: any;
  private readonly finalizeCachedTopologyKernel: any;
  private readonly planCacheStagingSeenClearKernel: any;
  private readonly clearCacheStagingKernel: any;
  private readonly stageCachedBodiesKernel: any;
  private readonly stageCachedContactsKernel: any;
  private readonly publishCachedTopologyKernel: any;
  private readonly fastGateCleanupKernel: any;
  private readonly serialDsuKernel: any;
  private readonly unionContactsKernel: any;
  private readonly pointerJumpKernel: any;
  private readonly finishUnionRoundKernels: readonly any[];
  private readonly clearUnionRoundContinuationKernels: readonly any[];
  private readonly compressParentsKernel: any;
  private readonly clearIslandsKernel: any;
  private readonly clearIslandsAfterKernel: any;
  private readonly reduceBodiesBeforeKernel: any;
  private readonly reduceContactsBeforeKernel: any;
  private readonly applyWakeKernel: any;
  private readonly freezeSleepingKernel: any;
  private readonly reduceBodiesAfterKernel: any;
  private readonly reduceContactsAfterKernel: any;
  private readonly updateDwellKernel: any;
  private readonly reduceDwellKernel: any;
  private readonly applySleepKernel: any;
  private readonly syncSleepFlagsKernel: any;
  private readonly syncSleepFlagsAfterKernel: any;
  private readonly maskActiveSleepingContactsKernel: any;
  private readonly selectSolverContactsKernel: any;
  private readonly compactAwakeSolverContactsKernel: any;
  private readonly finalizeSolverContactsKernel: any;
  private readonly filterSleepingContactsKernel: any;
  private readonly updateCandidatesKernel: any;
  private readonly resetCandidatesBeforeKernel: any;
  private readonly resetCandidatesAfterKernel: any;
  private readonly finalizePersistentGateKernel: any;

  private enabled = true;
  private disposed = false;
  private resetRequested = true;
  private wakeFlushPending = false;
  private mayHaveSleepingBodies = false;
  private preparedFrameId = -1;
  private preparedBodyCount = 0;
  private topologyGeneration: number | undefined;
  private appendStartBodyCount: number | null = null;
  private readbackGeneration = 0;
  private lastTelemetry: SleepingIslandsTelemetry | null = null;
  /**
   * CPU-side certificate derived only from a queue-ordered GPU telemetry map.
   * It can skip an entire fixed step, so every public mutation invalidates it
   * synchronously before any later step can consult it.
   */
  private zeroWorkEligible = false;
  private zeroWorkConfirmedFrameId = -1;
  private zeroWorkConfirmedBodyCount = 0;
  private zeroWorkConfirmedTopologyGeneration: number | null = null;
  private zeroWorkConfirmations = 0;
  private zeroWorkSkippedFixedSteps = 0;
  private zeroWorkInvalidations = 0;
  private mutationEpoch = 0;
  private sparseBroadphaseEligible = false;
  private sparseBroadphaseGeneration = 0;
  private sparseBroadphaseConfirmedFrameId = -1;
  private sparseBroadphaseConfirmedBodyCount = 0;
  private sparseBroadphaseConfirmedTopologyGeneration: number | null = null;
  private sparseBroadphaseConfirmedMutationEpoch = -1;
  /**
   * One-time proof for this GPU allocation. `confirmSubmittedFrame()` alone
   * only says the command graph was accepted; it cannot prove that Three's
   * storage attribute was materialized and written. The first queue-drained
   * telemetry map arms this only when the queue-ordered planner reported the
   * complete direct begin-frame dispatch for the active body range.
   */
  private sparseBodyStateGpuPublicationProven = false;
  private completedMutationEpoch = -1;
  private completedBodyCount = 0;
  private completedTopologyGeneration: number | null = null;
  private completedFrameId = -1;
  private completedFrameSubmitted = false;
  private completedGravity: readonly [number, number, number] | null = null;
  private zeroWorkConfirmedGravity: readonly [number, number, number] | null = null;
  private beforeSolveComputeGroups: any[][] = [];
  private afterSolveComputeGroups: any[][] = [];
  private beforeSolveComputeNodeCount = 0;
  private afterSolveComputeNodeCount = 0;

  constructor(inputs: SleepingIslandsStageInputs) {
    const {
      renderer,
      device,
      positions,
      quaternions,
      velocities,
      angularVelocities,
      shapes,
      initialPose,
      inertialPose,
      adaptiveSleepState,
      conservativeBodyFlags,
      bodyConstraintCounts,
      pairContacts,
      pairActivity,
      pairBodyContactCounts,
      pairBodyContactIndices,
      bodyState,
      maxBodies,
      maxPairContacts,
      maxActivePairContacts,
      maxCachedPairs,
      maxPairContactsPerBody,
      pairActiveContactsOffset,
      pairSolverContactsOffset = pairActiveContactsOffset,
      pairSolverContactsCapacity = 0,
      options = {},
    } = inputs;

    this.maxBodies = Math.max(1, Math.floor(maxBodies));
    this.maxPairContacts = Math.max(1, Math.floor(maxPairContacts));
    this.maxActivePairContacts = Math.max(1, Math.floor(maxActivePairContacts));
    if (!Number.isSafeInteger(maxCachedPairs) || maxCachedPairs < 1) {
      throw new Error('Sleeping islands: maxCachedPairs must be a positive safe integer.');
    }
    const requestedPairHashCapacity = Math.max(2, maxCachedPairs * 2);
    this.cachePairHashCapacity = 2 ** Math.ceil(Math.log2(requestedPairHashCapacity));
    if (
      !Number.isSafeInteger(this.cachePairHashCapacity)
      || this.cachePairHashCapacity > 0x40000000
    ) {
      throw new Error('Sleeping islands: canonical pair hash capacity is not representable.');
    }
    const maxWorkgroupsPerDimension = Math.max(
      1,
      Number(device.limits.maxComputeWorkgroupsPerDimension ?? 65_535),
    );
    const stagingClearWorkgroups = Math.ceil(Math.max(
      this.cachePairHashCapacity,
      Math.max(1, Math.ceil(this.maxPairContacts / 32)),
    ) / WORKGROUP_SIZE);
    if (stagingClearWorkgroups > maxWorkgroupsPerDimension) {
      throw new Error(
        `Sleeping islands: topology-cache clear requires ${stagingClearWorkgroups} `
        + `workgroups, exceeding the device limit ${maxWorkgroupsPerDimension}.`,
      );
    }
    this.pairActiveContactsOffset = Math.max(0, Math.floor(pairActiveContactsOffset));
    this.pairSolverContactsOffset = Math.max(0, Math.floor(pairSolverContactsOffset));
    this.pairSolverContactsCapacity = Math.max(0, Math.floor(pairSolverContactsCapacity));
    this.solverContactCompactionEnabled = this.pairSolverContactsOffset
      !== this.pairActiveContactsOffset
      && this.pairSolverContactsCapacity >= this.maxActivePairContacts;
    if (!renderer || !device) {
      throw new Error(
        'Sleeping islands require a materialized WebGPU renderer and device '
        + 'for GPU-only topology cache buffers.',
      );
    }
    if (
      this.maxActivePairContacts >= SOLVER_CONTACT_CANONICAL_ALIAS_BIT
      || this.pairSolverContactsCapacity >= SOLVER_CONTACT_CANONICAL_ALIAS_BIT
    ) {
      throw new Error('Sleeping islands: solver-contact capacity collides with alias bit.');
    }
    this.sleepFrames = Math.max(1, Math.floor(options.sleepFrames ?? 60));
    // Pointer jumping halves every remaining parent-chain depth per round;
    // log2(maxBodies) rounds plus four validation slots cover the worst case.
    this.maxRootDepth = Math.ceil(Math.log2(this.maxBodies)) + 4;
    // Shiloach-Vishkin-style root hooking + pointer jumping converges in
    // O(log N) rounds. Callers may raise this for diagnostics, but never lower
    // it below the proven body-count bound.
    this.unionPasses = Math.max(
      this.maxRootDepth,
      Math.min(64, Math.floor(options.unionPasses ?? this.maxRootDepth)),
    );

    if (pairContacts.count < this.maxPairContacts * CONTACT_RECORD_VEC4S) {
      throw new Error('Sleeping islands: pair contact buffer is too small.');
    }
    if (pairActivity.count < this.pairActiveContactsOffset + this.maxActivePairContacts + 1) {
      throw new Error('Sleeping islands: active-contact list is too small.');
    }
    if (
      this.solverContactCompactionEnabled
      && pairActivity.count
        < this.pairSolverContactsOffset + this.pairSolverContactsCapacity + 1
    ) {
      throw new Error('Sleeping islands: solver-contact scratch list is too small.');
    }

    const fixedPairBodyWords = this.maxBodies * maxPairContactsPerBody;
    const pairBodyOverflowCounterOffset = fixedPairBodyWords;
    const pairBodyOverflowHeadOffset = pairBodyOverflowCounterOffset + 1;
    const pairBodyOverflowNodeOffset = pairBodyOverflowHeadOffset + this.maxBodies;
    const pairBodyOverflowCapacity = Math.floor(
      (pairBodyContactIndices.count - pairBodyOverflowNodeOffset) / 2,
    );
    if (pairBodyOverflowCapacity < 1) {
      throw new Error('Sleeping islands: pair-body spill pool is missing.');
    }

    const sleepLinear = Math.max(0, options.sleepLinearSpeed ?? 0.025);
    const sleepAngular = Math.max(0, options.sleepAngularSpeed ?? 0.025);
    const wakeLinear = Math.max(sleepLinear * 2, options.wakeLinearSpeed ?? sleepLinear * 3);
    const wakeAngular = Math.max(sleepAngular * 2, options.wakeAngularSpeed ?? sleepAngular * 3);
    const wakePositionDelta = Math.max(1e-7, options.wakePositionDelta ?? 1e-3);
    const wakeAngularDelta = Math.max(1e-7, options.wakeAngularDelta ?? 2e-3);
    const sleepPositionCorrection = Math.max(
      1e-8,
      options.sleepPositionCorrection ?? 2e-4,
    );
    const sleepAngularCorrection = Math.max(
      1e-8,
      options.sleepAngularCorrection ?? 5e-4,
    );
    const sleepContactPenetration = Math.max(
      0,
      options.sleepContactPenetration ?? 6e-3,
    );
    const wakeContactPenetration = Math.max(
      sleepContactPenetration * 2,
      options.wakeContactPenetration ?? 2e-2,
    );
    const supportNormalCosine = Math.max(
      0,
      Math.min(1, options.supportNormalCosine ?? 0.5),
    );
    const gravity = options.gravity ?? [0, -9.81, 0];
    const supportDirection = new THREE.Vector3(-gravity[0], -gravity[1], -gravity[2]);
    if (supportDirection.lengthSq() > 1e-12) supportDirection.normalize();
    else supportDirection.set(0, 0, 0);

    if (bodyState && bodyState.count < this.maxBodies) {
      throw new Error(
        `Sleeping islands: shared bodyState capacity ${bodyState.count} is below ${this.maxBodies}.`,
      );
    }
    this.bodyStateAttr = bodyState
      ?? new StorageBufferAttribute(new Uint32Array(this.maxBodies), 1);
    this.bodyStateAttr.name = 'Sleeping Island Body State';
    this.stableFramesAttr = new StorageBufferAttribute(new Uint32Array(this.maxBodies), 1);
    this.stableFramesAttr.name = 'Sleeping Island Stable Frames';
    this.candidateFramesAttr = new StorageBufferAttribute(
      new Uint32Array(this.maxBodies),
      1,
    );
    this.candidateFramesAttr.name = 'Sleeping Island Awake Fast Candidates';
    this.gateControlAttr = new StorageBufferAttribute(
      new Uint32Array(GATE_CONTROL_WORDS),
      1,
    );
    this.gateControlAttr.name = 'Sleeping Island Persistent GPU Gate';
    this.parentAttr = new StorageBufferAttribute(new Uint32Array(this.maxBodies), 1);
    this.parentAttr.name = 'Sleeping Island DSU Parent';
    this.unionChangedAttr = new StorageBufferAttribute(new Uint32Array(1), 1);
    this.unionChangedAttr.name = 'Sleeping Island Union Changed';
    this.islandDataAttr = new StorageBufferAttribute(
      new Uint32Array(this.maxBodies * ISLAND_WORDS),
      1,
    );
    this.islandDataAttr.name = 'Sleeping Island Aggregate';
    this.poseHistoryAttr = new StorageBufferAttribute(
      new Float32Array(this.maxBodies * BODY_HISTORY_VEC4S * 4),
      4,
    );
    this.poseHistoryAttr.name = 'Sleeping Island Pose History';
    this.telemetryAttr = new StorageBufferAttribute(new Uint32Array(TELEMETRY_WORDS), 1);
    this.telemetryAttr.name = 'Sleeping Island Telemetry';
    const cacheHeaderWords = new Uint32Array(CACHE_HEADER_WORDS);
    // Canonical endpoint pairs are packed losslessly as 16+16 bits. Larger
    // body capacities retain correct full DSU behavior but disable cache hits.
    if (this.maxBodies > 0x1_0000) cacheHeaderWords[CACHE_DISABLED] = 1;
    this.cacheHeaderAttr = new StorageBufferAttribute(cacheHeaderWords, 1);
    this.cacheHeaderAttr.name = 'Sleeping Island Published Topology Cache Header';
    this.cacheValidationAttr = new StorageBufferAttribute(
      new Uint32Array(CACHE_VALIDATION_WORDS),
      1,
    );
    this.cacheValidationAttr.name = 'Sleeping Island Topology Cache Validation';
    let cachedBodyRootAllocation: GpuOnlyStorageAllocation<Uint32Array> | undefined;
    let cachedPairKeyAllocation: GpuOnlyStorageAllocation<Uint32Array> | undefined;
    let cacheSeenBitsAllocation: GpuOnlyStorageAllocation<Uint32Array> | undefined;
    let cachePairSeenBitsAllocation: GpuOnlyStorageAllocation<Uint32Array> | undefined;
    try {
      cachedBodyRootAllocation = createGpuOnlyStorageAttribute(renderer, device, {
        scalarType: 'uint32',
        count: this.maxBodies,
        itemSize: 1,
        label: 'Sleeping Island Cached Body Roots (GPU-only)',
      });
      cachedPairKeyAllocation = createGpuOnlyStorageAttribute(renderer, device, {
        scalarType: 'uint32',
        count: this.cachePairHashCapacity,
        itemSize: 1,
        label: 'Sleeping Island Cached Canonical Pair Hash (GPU-only)',
      });
      cacheSeenBitsAllocation = createGpuOnlyStorageAttribute(renderer, device, {
        scalarType: 'uint32',
        count: Math.max(1, Math.ceil(this.maxPairContacts / 32)),
        itemSize: 1,
        label: 'Sleeping Island Cache Duplicate Bitset (GPU-only)',
      });
      cachePairSeenBitsAllocation = createGpuOnlyStorageAttribute(renderer, device, {
        scalarType: 'uint32',
        count: Math.max(1, Math.ceil(this.cachePairHashCapacity / 32)),
        itemSize: 1,
        label: 'Sleeping Island Cache Pair Seen Bitset (GPU-only)',
      });
    } catch (error) {
      cachePairSeenBitsAllocation?.destroy();
      cacheSeenBitsAllocation?.destroy();
      cachedPairKeyAllocation?.destroy();
      cachedBodyRootAllocation?.destroy();
      throw error;
    }
    this.cachedBodyRootAllocation = cachedBodyRootAllocation;
    this.cachedPairKeyAllocation = cachedPairKeyAllocation;
    this.cacheSeenBitsAllocation = cacheSeenBitsAllocation;
    this.cachePairSeenBitsAllocation = cachePairSeenBitsAllocation;
    this.cachedBodyRootAttr = cachedBodyRootAllocation.attribute;
    this.cachedBodyRootAttr.name = 'Sleeping Island Cached Body Roots';
    this.cachedPairKeyAttr = cachedPairKeyAllocation.attribute;
    this.cachedPairKeyAttr.name = 'Sleeping Island Cached Canonical Pair Hash';
    this.cacheSeenBitsAttr = cacheSeenBitsAllocation.attribute;
    this.cacheSeenBitsAttr.name = 'Sleeping Island Cache Duplicate Bitset';
    this.cachePairSeenBitsAttr = cachePairSeenBitsAllocation.attribute;
    this.cachePairSeenBitsAttr.name = 'Sleeping Island Cache Pair Seen Bitset';
    try {
    this.contactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.contactDispatchIndirectAttr.name = 'Sleeping Island Contact Dispatch Indirect';
    this.fullDsuContactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.fullDsuContactDispatchIndirectAttr.name = 'Sleeping Island Full DSU Contact Dispatch';
    this.fullDsuBodyDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.fullDsuBodyDispatchIndirectAttr.name = 'Sleeping Island Full DSU Body Dispatch';
    this.cacheValidationContactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.cacheValidationContactDispatchIndirectAttr.name = 'Sleeping Island Cache Contact Validation Dispatch';
    this.cacheValidationBodyDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.cacheValidationBodyDispatchIndirectAttr.name = 'Sleeping Island Cache Body Validation Dispatch';
    this.cacheSeenClearDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.cacheSeenClearDispatchIndirectAttr.name = 'Sleeping Island Cache Seen Clear Dispatch';
    this.cacheStagingSeenClearDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.cacheStagingSeenClearDispatchIndirectAttr.name =
      'Sleeping Island Cache Staging Seen Clear Dispatch';
    this.unionContactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.unionContactDispatchIndirectAttr.name = 'Sleeping Island Union Contact Dispatch Indirect';
    this.unionBodyDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.unionBodyDispatchIndirectAttr.name = 'Sleeping Island Union Body Dispatch Indirect';
    this.stageBodyDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.stageBodyDispatchIndirectAttr.name = 'Sleeping Island Quiet Body Dispatch Indirect';
    this.fastCleanupDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.fastCleanupDispatchIndirectAttr.name = 'Sleeping Island Active Cleanup Dispatch Indirect';
    this.serialDsuDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.serialDsuDispatchIndirectAttr.name = 'Sleeping Island Serial DSU Dispatch Indirect';
    this.unionRoundDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.unionRoundDispatchIndirectAttr.name = 'Sleeping Island Union Round Dispatch Indirect';
    // A finalizer cannot write the same buffer that supplies its own indirect
    // arguments: WebGPU gives one dispatch a single usage scope. Give every
    // bounded round a distinct continuation argument instead. Round N writes
    // only round N+1, so convergence can zero the complete remaining command
    // chain without an incompatible STORAGE+INDIRECT binding or a CPU readback.
    const unionRoundContinuationDispatches = Array.from(
      { length: this.unionPasses },
      (_, round) => {
        const attribute = new IndirectStorageBufferAttribute(
          new Uint32Array([0, 1, 1]),
          1,
        );
        attribute.name = `Sleeping Island Union Round ${round + 2} Dispatch Indirect`;
        return attribute;
      },
    );
    this.unionRoundDispatchIndirectAttrs = Object.freeze([
      this.unionRoundDispatchIndirectAttr,
      ...unionRoundContinuationDispatches,
    ]);
    this.solverContactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.solverContactDispatchIndirectAttr.name = 'Sleeping Island Solver Contact Dispatch Indirect';
    this.candidateUpdateDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.candidateUpdateDispatchIndirectAttr.name = 'Sleeping Island Candidate Update Dispatch Indirect';
    this.candidateResetBeforeDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.candidateResetBeforeDispatchIndirectAttr.name = 'Sleeping Island Candidate Pre-Reset Dispatch Indirect';
    this.candidateResetAfterDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.candidateResetAfterDispatchIndirectAttr.name = 'Sleeping Island Candidate Post-Reset Dispatch Indirect';

    const helpers = wgsl(/* wgsl */`
      fn sleepFinite(v: f32) -> bool {
        return v == v && abs(v) <= 3.402823466e38;
      }

      fn sleepFinite3(v: vec3f) -> bool {
        return sleepFinite(v.x) && sleepFinite(v.y) && sleepFinite(v.z);
      }

      fn sleepFinite4(v: vec4f) -> bool {
        return sleepFinite3(v.xyz) && sleepFinite(v.w);
      }

      fn sleepQuaternionDelta(a: vec4f, b: vec4f) -> f32 {
        let a2 = dot(a, a);
        let b2 = dot(b, b);
        if (!sleepFinite(a2) || !sleepFinite(b2) || a2 <= 1e-12 || b2 <= 1e-12) {
          return 3.141592654;
        }
        // acos(dot(q0, q1)) is catastrophically ill-conditioned near the
        // identity in f32: a one-ULP normalization difference reports about
        // 6.9e-4 rad, larger than the default quiet-rotation threshold.  Form
        // q0 * conjugate(q1) and use atan2(|v|, |w|) instead.  The common
        // quaternion scale cancels, and the vector part remains accurate as
        // the true angle approaches zero.
        let relativeVector = b.w * a.xyz - a.w * b.xyz - cross(a.xyz, b.xyz);
        let relativeScalar = dot(a, b);
        let vectorLength = length(relativeVector);
        if (!sleepFinite(vectorLength) || !sleepFinite(relativeScalar)) {
          return 3.141592654;
        }
        return 2.0 * atan2(vectorLength, abs(relativeScalar));
      }

      fn sleepBoundingRadius(shape: vec4f) -> f32 {
        let half = max(shape.yzw, vec3f(0.0));
        let shapeType = (bitcast<u32>(shape.x) >> 14u) & 0x3u;
        return select(length(half), half.x, shapeType == 1u);
      }

      fn sleepShapeValid(shape: vec4f) -> bool {
        // shape.x is an arbitrary packed u32 bit pattern. Valid collision-mask
        // metadata commonly decodes as a NaN f32, so it must never pass
        // through floating-point finite checks.
        let shapeType = (bitcast<u32>(shape.x) >> 14u) & 0x3u;
        return sleepFinite3(shape.yzw)
          && all(shape.yzw >= vec3f(0.0))
          && (shapeType == 0u || shapeType == 1u);
      }

      fn sleepFindRoot(
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        start: u32,
        bodyCount: u32,
      ) -> u32 {
        if (start >= bodyCount) { return 0xffffffffu; }
        var root = start;
        for (var depth = 0u; depth < ${this.maxRootDepth}u; depth++) {
          let next = atomicLoad(&parents[root]);
          if (next == root) { return root; }
          if (next >= bodyCount) { return 0xffffffffu; }
          root = next;
        }
        return 0xffffffffu;
      }

      fn sleepSerialFindRoot(
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        start: u32,
        bodyCount: u32,
      ) -> u32 {
        if (start >= bodyCount) { return 0xffffffffu; }
        var root = start;
        for (var depth = 0u; depth <= ${SERIAL_DSU_BODY_THRESHOLD}u; depth++) {
          let next = atomicLoad(&parents[root]);
          if (next == root) { return root; }
          if (next >= bodyCount) { return 0xffffffffu; }
          root = next;
        }
        return 0xffffffffu;
      }
    `);

    const clearTelemetryShader = wgslFn(/* wgsl */`
      fn compute(
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        frameId: u32,
        initialFlags: u32,
      ) -> void {
        for (var i = 0u; i < ${TELEMETRY_WORDS}u; i++) {
          atomicStore(&telemetry[i], 0u);
        }
        atomicStore(&telemetry[${TELEMETRY_FRAME}u], frameId);
        atomicStore(&telemetry[${TELEMETRY_FLAGS}u], initialFlags);
        if ((initialFlags & ${FRAME_INCOMPATIBLE | FRAME_FORCED_WAKE | FRAME_RESET}u) != 0u) {
          atomicStore(&telemetry[${TELEMETRY_FAST_GATE}u], 1u);
        }
      }
    `);
    this.clearTelemetryKernel = clearTelemetryShader({
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      frameId: uniform(0),
      initialFlags: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Clear Telemetry');
    assertStorageBufferBudget('Sleeping Islands Clear Telemetry', 1);

    const beginFrameShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        shapes: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        conservativeBodyFlags: ptr<storage, array<u32>, read>,
        bodyState: ptr<storage, array<u32>, read_write>,
        stableFrames: ptr<storage, array<u32>, read_write>,
        poseHistory: ptr<storage, array<vec4f>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        compatible: u32,
        resetState: u32,
        forceWake: u32,
        initializeFromBody: u32,
        wakeLinearSpeed: f32,
        wakeAngularSpeed: f32,
        wakePositionDelta: f32,
        wakeAngularDelta: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        if (gid == 0u && resetState == 0u && initializeFromBody < bodyCount) {
          atomicStore(&telemetry[${TELEMETRY_APPEND_PRESERVED}u], 1u);
        }

        let pos = positions[gid];
        let shape = shapes[gid];
        let q = quaternions[gid];
        let v = velocities[gid];
        let w = angularVelocities[gid];
        let historyBase = gid * ${BODY_HISTORY_VEC4S}u;
        let oldState = bodyState[gid];
        let appendedBody = gid >= initializeFromBody;
        let initialized = (oldState & ${BODY_INITIALIZED}u) != 0u
          && resetState == 0u
          && !appendedBody;
        let wasSleeping = (oldState & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u;
        let dynamicBody = sleepFinite(pos.w) && pos.w > 0.0;
        let conservative = conservativeBodyFlags[gid] != 0u;

        var state = ${BODY_INITIALIZED}u;
        if (compatible != 0u && wasSleeping && initialized) {
          state |= ${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
        }
        if (conservative) {
          state |= ${BODY_CONSERVATIVE | BODY_LOCAL_WAKE}u;
        }

        let valid = sleepFinite4(pos) && sleepShapeValid(shape) && sleepFinite4(q)
          && sleepFinite4(v) && sleepFinite4(w) && pos.w >= 0.0;
        if (!valid) {
          state |= ${BODY_LOCAL_FAULT | BODY_LOCAL_WAKE}u;
          state &= ~${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
          stableFrames[gid] = 0u;
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          atomicStore(&telemetry[${TELEMETRY_FAST_GATE}u], 1u);
        }

        if (!initialized || resetState != 0u) {
          poseHistory[historyBase] = pos;
          poseHistory[historyBase + 1u] = q;
          state |= ${BODY_LOCAL_WAKE}u;
          // Static bodies do not participate in the body-island reduction.
          // A pure tail append must therefore seed the existing static-contact
          // wake path explicitly, otherwise a newly inserted static obstacle
          // can overlap a sleeping island without waking it for correction.
          if (appendedBody && !dynamicBody && resetState == 0u) {
            state |= ${BODY_STATIC_MOVED}u;
          }
          state &= ~${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
          stableFrames[gid] = 0u;
        } else {
          let previousPos = poseHistory[historyBase];
          let previousQ = poseHistory[historyBase + 1u];
          let dp = pos.xyz - previousPos.xyz;
          let moved = dot(dp, dp) > wakePositionDelta * wakePositionDelta
            || sleepQuaternionDelta(q, previousQ) > wakeAngularDelta;
          if (moved) {
            // Awake bodies are expected to move while AVBD converges; their
            // dwell eligibility is measured from velocity and solve
            // correction after the solve. Treating ordinary awake motion as
            // an external wake reset made stableFrames unable to accumulate
            // through harmless f32/contact jitter. Pose-delta wake detection
            // is only needed once a dynamic body is actually sleeping.
            if (dynamicBody) {
              if (wasSleeping) {
                state |= ${BODY_LOCAL_WAKE}u;
              }
            } else {
              state |= ${BODY_STATIC_MOVED}u;
            }
          }
        }

        if (dynamicBody) {
          let boundingRadius = sleepBoundingRadius(shape);
          let speed2 = dot(v.xyz, v.xyz);
          let angularSurfaceSpeed2 = dot(w.xyz, w.xyz) * boundingRadius * boundingRadius;
          if (
            speed2 > wakeLinearSpeed * wakeLinearSpeed
            || angularSurfaceSpeed2 > wakeAngularSpeed * wakeAngularSpeed
          ) {
            state |= ${BODY_LOCAL_WAKE}u;
          }
        } else {
          state &= ~${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
          stableFrames[gid] = 0u;
        }

        if (compatible == 0u || forceWake != 0u) {
          if (wasSleeping && dynamicBody) {
            atomicAdd(&telemetry[${TELEMETRY_WAKE_EVENTS}u], 1u);
          }
          state &= ~${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
          stableFrames[gid] = 0u;
          poseHistory[historyBase] = pos;
          poseHistory[historyBase + 1u] = q;
        }
        poseHistory[historyBase + 2u] = pos;
        poseHistory[historyBase + 3u] = q;
        bodyState[gid] = state;
      }
    `, [helpers]);
    this.beginFrameKernel = beginFrameShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', this.maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', this.maxBodies).toReadOnly(),
      conservativeBodyFlags: storage(conservativeBodyFlags, 'uint', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies),
      stableFrames: storage(this.stableFramesAttr, 'uint', this.maxBodies),
      poseHistory: storage(
        this.poseHistoryAttr,
        'vec4f',
        this.maxBodies * BODY_HISTORY_VEC4S,
      ),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      compatible: uniform(1),
      resetState: uniform(0),
      forceWake: uniform(0),
      initializeFromBody: uniform(this.maxBodies),
      wakeLinearSpeed: uniform(wakeLinear),
      wakeAngularSpeed: uniform(wakeAngular),
      wakePositionDelta: uniform(wakePositionDelta),
      wakeAngularDelta: uniform(wakeAngularDelta),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Begin Frame');
    assertStorageBufferBudget('Sleeping Islands Begin Frame', 10);

    const planPersistentGateShader = wgslFn(/* wgsl */`
      fn compute(
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        cacheHeader: ptr<storage, array<atomic<u32>>, read_write>,
        candidateUpdateDispatch: ptr<storage, array<u32>, read_write>,
        candidateResetBeforeDispatch: ptr<storage, array<u32>, read_write>,
        candidateResetAfterDispatch: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
        dynamicBodyCount: u32,
        topologyKnown: u32,
        topologyGeneration: u32,
      ) -> void {
        let bodyWorkgroups = (bodyCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        // beforeSolve records beginFrame as a direct bodyWorkgroups dispatch
        // immediately before this one. Reaching the planner is therefore the
        // GPU-side receipt that every active bodyState row was published,
        // without serializing all bodies on one global atomic cache line.
        atomicStore(&telemetry[${TELEMETRY_INITIALIZED_BODIES}u], bodyCount);
        // Body mass is immutable after addBody(), so the CPU can maintain this
        // exact f32-classified count and publish it once instead of making all
        // dynamic beginFrame lanes contend on one telemetry cache line.
        atomicStore(
          &telemetry[${TELEMETRY_DYNAMIC_BODIES}u],
          min(dynamicBodyCount, bodyCount),
        );
        let previousEpoch = atomicAdd(&gateControl[${GATE_SUBSTEP_EPOCH}u], 1u);
        var substepEpoch = previousEpoch + 1u;
        // Epoch zero is reserved for freshly allocated diagnostics. A wrap is
        // a once-per-2^32-substep reset/wake event, never an alias of an old
        // substep stamp.
        let epochWrapped = substepEpoch == 0u;
        if (epochWrapped) {
          substepEpoch = 1u;
          atomicStore(&gateControl[${GATE_SUBSTEP_EPOCH}u], substepEpoch);
          atomicStore(&telemetry[${TELEMETRY_FAST_GATE}u], 1u);
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_RESET | FRAME_FORCED_WAKE}u);
          atomicStore(&cacheHeader[${CACHE_VALID}u], 0u);
        }

        let forcedGate = atomicLoad(&telemetry[${TELEMETRY_FAST_GATE}u]) != 0u;
        let sleepingBodies = atomicLoad(&gateControl[${GATE_SLEEPING_BODIES}u]);
        let candidateReady = atomicLoad(&gateControl[${GATE_CANDIDATE_READY}u]) != 0u;
        let previousMode = atomicLoad(&gateControl[${GATE_MODE}u]);
        let fullValidate = !forcedGate && (
          sleepingBodies > 0u
          || candidateReady
          || previousMode == ${GATE_MODE_FULL_VALIDATE}u
        );
        let cacheHeaderExact = topologyKnown != 0u
          && atomicLoad(&cacheHeader[${CACHE_VALID}u]) != 0u
          && atomicLoad(&cacheHeader[${CACHE_DISABLED}u]) == 0u
          && atomicLoad(&cacheHeader[${CACHE_BODY_COUNT}u]) == bodyCount
          && atomicLoad(&cacheHeader[${CACHE_TOPOLOGY_GENERATION}u]) == topologyGeneration;
        let validateCache = fullValidate && cacheHeaderExact;

        candidateUpdateDispatch[0u] = select(bodyWorkgroups, 0u, fullValidate || forcedGate);
        candidateUpdateDispatch[1u] = 1u;
        candidateUpdateDispatch[2u] = 1u;
        candidateResetBeforeDispatch[0u] = select(0u, bodyWorkgroups, forcedGate);
        candidateResetBeforeDispatch[1u] = 1u;
        candidateResetBeforeDispatch[2u] = 1u;
        candidateResetAfterDispatch[0u] = 0u;
        candidateResetAfterDispatch[1u] = 1u;
        candidateResetAfterDispatch[2u] = 1u;

        if (forcedGate) {
          atomicStore(&gateControl[${GATE_MODE}u], ${GATE_MODE_AWAKE_FAST}u);
          atomicStore(&gateControl[${GATE_PATH}u], ${GATE_PATH_NONE}u);
          atomicStore(&gateControl[${GATE_SLEEPING_BODIES}u], 0u);
          atomicStore(&gateControl[${GATE_CANDIDATE_READY}u], 0u);
          atomicStore(&gateControl[${GATE_MAX_CANDIDATE}u], 0u);
          atomicStore(&cacheHeader[${CACHE_VALID}u], 0u);
        } else if (fullValidate) {
          atomicStore(&gateControl[${GATE_MODE}u], ${GATE_MODE_FULL_VALIDATE}u);
          atomicStore(&gateControl[${GATE_PATH}u], select(
            ${GATE_PATH_FULL_VALIDATE}u,
            ${GATE_PATH_CACHE_VALIDATE}u,
            validateCache,
          ));
          if (!validateCache) {
            let fullCount = atomicAdd(
              &gateControl[${GATE_FULL_DSU_SUBSTEPS}u],
              1u,
            ) + 1u;
            atomicStore(&telemetry[${TELEMETRY_FULL_DSU_THIS_SUBSTEP}u], 1u);
            atomicStore(&telemetry[${TELEMETRY_FULL_DSU_SUBSTEPS}u], fullCount);
            atomicStore(&telemetry[${TELEMETRY_TOPOLOGY_PATH}u], ${GATE_PATH_FULL_VALIDATE}u);
          } else {
            atomicStore(&telemetry[${TELEMETRY_TOPOLOGY_PATH}u], ${GATE_PATH_CACHE_VALIDATE}u);
          }
        } else {
          // No GPU sleeping body exists and no candidate is close enough to
          // sleep. Preserve the exact canonical contact stream and suppress
          // every DSU/island dispatch for this real physics substep.
          atomicStore(&telemetry[${TELEMETRY_FAST_GATE}u], 1u);
          atomicStore(&gateControl[${GATE_MODE}u], ${GATE_MODE_AWAKE_FAST}u);
          atomicStore(&gateControl[${GATE_PATH}u], ${GATE_PATH_AWAKE_FAST}u);
          let fastCount = atomicAdd(
            &gateControl[${GATE_AWAKE_FAST_SUBSTEPS}u],
            1u,
          ) + 1u;
          atomicStore(&telemetry[${TELEMETRY_AWAKE_FAST_GATED}u], 1u);
          atomicStore(&telemetry[${TELEMETRY_AWAKE_FAST_SUBSTEPS}u], fastCount);
          atomicStore(&telemetry[${TELEMETRY_TOPOLOGY_PATH}u], ${GATE_PATH_AWAKE_FAST}u);
        }

        atomicStore(&telemetry[${TELEMETRY_GATE_MODE}u], select(
          ${GATE_MODE_AWAKE_FAST}u,
          ${GATE_MODE_FULL_VALIDATE}u,
          fullValidate,
        ));
        atomicStore(&telemetry[${TELEMETRY_SUBSTEP_EPOCH}u], substepEpoch);
        atomicStore(
          &telemetry[${TELEMETRY_MAX_CANDIDATE_FRAMES}u],
          atomicLoad(&gateControl[${GATE_MAX_CANDIDATE}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_AWAKE_FAST_SUBSTEPS}u],
          atomicLoad(&gateControl[${GATE_AWAKE_FAST_SUBSTEPS}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_FULL_DSU_SUBSTEPS}u],
          atomicLoad(&gateControl[${GATE_FULL_DSU_SUBSTEPS}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_CANDIDATE_FALLBACKS}u],
          atomicLoad(&gateControl[${GATE_CANDIDATE_FALLBACKS}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_CACHED_ROOT_HITS}u],
          atomicLoad(&gateControl[${GATE_CACHED_ROOT_HITS}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_CACHED_ROOT_MISSES}u],
          atomicLoad(&gateControl[${GATE_CACHED_ROOT_MISSES}u]),
        );
      }
    `);
    this.planPersistentGateKernel = planPersistentGateShader({
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      cacheHeader: storage(this.cacheHeaderAttr, 'uint', CACHE_HEADER_WORDS).toAtomic(),
      candidateUpdateDispatch: storage(this.candidateUpdateDispatchIndirectAttr, 'uint', 3),
      candidateResetBeforeDispatch: storage(
        this.candidateResetBeforeDispatchIndirectAttr,
        'uint',
        3,
      ),
      candidateResetAfterDispatch: storage(
        this.candidateResetAfterDispatchIndirectAttr,
        'uint',
        3,
      ),
      bodyCount: uniform(0),
      dynamicBodyCount: uniform(0),
      topologyKnown: uniform(0),
      topologyGeneration: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Plan Persistent GPU Gate');
    assertStorageBufferBudget('Sleeping Islands Plan Persistent GPU Gate', 6);

    const initializeDsuShader = wgslFn(/* wgsl */`
      fn compute(
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        unionChanged: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid == 0u) {
          // The previous graph may have stopped before its last bounded round.
          // Re-arm the controller at the same dispatch boundary that publishes
          // fresh singleton labels. Every later finalizer atomically exchanges
          // this word back to zero for the following round.
          atomicStore(&unionChanged[0u], 0u);
          atomicStore(&telemetry[${TELEMETRY_DSU_ROUNDS_EXECUTED}u], 0u);
          atomicStore(
            &telemetry[${TELEMETRY_DSU_DISPATCHES_SAVED}u],
            ${this.unionPasses
              - Math.ceil(this.unionPasses / DSU_CONTINUATION_CLEAR_BINDINGS)}u,
          );
        }
        if (gid >= bodyCount) { return; }
        atomicStore(&parents[gid], gid);
      }
    `);
    this.initializeDsuKernel = initializeDsuShader({
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      unionChanged: storage(this.unionChangedAttr, 'uint', 1).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Initialize DSU Labels');
    assertStorageBufferBudget('Sleeping Islands Initialize DSU Labels', 3);

    const buildContactDispatchShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<u32>, read>,
        dispatchIndirect: ptr<storage, array<u32>, read_write>,
        stageBodyDispatchIndirect: ptr<storage, array<u32>, read_write>,
        fastCleanupDispatchIndirect: ptr<storage, array<u32>, read_write>,
        solverContactDispatchIndirect: ptr<storage, array<u32>, read_write>,
        cacheValidationContactDispatch: ptr<storage, array<u32>, read_write>,
        cacheValidationBodyDispatch: ptr<storage, array<u32>, read_write>,
        cacheSeenClearDispatch: ptr<storage, array<u32>, read_write>,
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
      ) -> void {
        let requested = pairActivity[${pairActiveContactsOffset}u];
        let bounded = min(requested, ${this.maxActivePairContacts}u);
        let bodyWorkgroups = (bodyCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        let path = atomicLoad(&gateControl[${GATE_PATH}u]);
        let fastActive = path == ${GATE_PATH_NONE}u || path == ${GATE_PATH_AWAKE_FAST}u;
        let validateCache = path == ${GATE_PATH_CACHE_VALIDATE}u;
        let quietContactWorkgroups = select(
          (bounded + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u,
          0u,
          fastActive,
        );
        let quietBodyWorkgroups = select(bodyWorkgroups, 0u, fastActive);
        dispatchIndirect[0] = quietContactWorkgroups;
        dispatchIndirect[1] = 1u;
        dispatchIndirect[2] = 1u;
        stageBodyDispatchIndirect[0] = quietBodyWorkgroups;
        stageBodyDispatchIndirect[1] = 1u;
        stageBodyDispatchIndirect[2] = 1u;
        fastCleanupDispatchIndirect[0] = select(0u, bodyWorkgroups, fastActive);
        fastCleanupDispatchIndirect[1] = 1u;
        fastCleanupDispatchIndirect[2] = 1u;
        cacheValidationContactDispatch[0] = select(
          0u,
          (bounded + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u,
          validateCache,
        );
        cacheValidationContactDispatch[1] = 1u;
        cacheValidationContactDispatch[2] = 1u;
        cacheValidationBodyDispatch[0] = select(0u, bodyWorkgroups, validateCache);
        cacheValidationBodyDispatch[1] = 1u;
        cacheValidationBodyDispatch[2] = 1u;
        cacheSeenClearDispatch[0] = select(
          0u,
          (${Math.max(
            Math.max(1, Math.ceil(this.maxPairContacts / 32)),
            Math.max(1, Math.ceil(this.cachePairHashCapacity / 32)),
          )}u + ${WORKGROUP_SIZE - 1}u)
            / ${WORKGROUP_SIZE}u,
          validateCache,
        );
        cacheSeenClearDispatch[1] = 1u;
        cacheSeenClearDispatch[2] = 1u;
        // Solver-list compaction must still run through forced-wake/reset and
        // incompatible frames. Unlike the quiet-island lanes above, its input
        // dispatch is therefore based on the canonical active count alone.
        solverContactDispatchIndirect[0] =
          (bounded + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        solverContactDispatchIndirect[1] = 1u;
        solverContactDispatchIndirect[2] = 1u;
        atomicStore(&telemetry[${TELEMETRY_ACTIVE_CONTACTS}u], bounded);
        if (${this.solverContactCompactionEnabled ? 0 : 1}u != 0u) {
          atomicStore(&telemetry[${TELEMETRY_SOLVER_CONTACTS}u], bounded);
        }
        if (requested > ${this.maxActivePairContacts}u) {
          atomicOr(
            &telemetry[${TELEMETRY_FLAGS}u],
            ${FRAME_FAIL_OPEN | FRAME_ACTIVE_LIST_OVERFLOW}u,
          );
        }
      }
    `);
    this.buildContactDispatchKernel = buildContactDispatchShader({
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      dispatchIndirect: storage(this.contactDispatchIndirectAttr, 'uint', 3),
      stageBodyDispatchIndirect: storage(this.stageBodyDispatchIndirectAttr, 'uint', 3),
      fastCleanupDispatchIndirect: storage(this.fastCleanupDispatchIndirectAttr, 'uint', 3),
      solverContactDispatchIndirect: storage(this.solverContactDispatchIndirectAttr, 'uint', 3),
      cacheValidationContactDispatch: storage(
        this.cacheValidationContactDispatchIndirectAttr,
        'uint',
        3,
      ),
      cacheValidationBodyDispatch: storage(
        this.cacheValidationBodyDispatchIndirectAttr,
        'uint',
        3,
      ),
      cacheSeenClearDispatch: storage(this.cacheSeenClearDispatchIndirectAttr, 'uint', 3),
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Build Contact Dispatch');
    assertStorageBufferBudget('Sleeping Islands Build Contact Dispatch', 10);

    const planTopologyDispatchShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<u32>, read>,
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        cacheHeader: ptr<storage, array<atomic<u32>>, read_write>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        fullDsuContactDispatch: ptr<storage, array<u32>, read_write>,
        fullDsuBodyDispatch: ptr<storage, array<u32>, read_write>,
        unionContactDispatch: ptr<storage, array<u32>, read_write>,
        unionBodyDispatch: ptr<storage, array<u32>, read_write>,
        serialDsuDispatch: ptr<storage, array<u32>, read_write>,
        unionRoundDispatch: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
      ) -> void {
        let bounded = min(
          pairActivity[${pairActiveContactsOffset}u],
          ${this.maxActivePairContacts}u,
        );
        let contactWorkgroups = (bounded + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        let bodyWorkgroups = (bodyCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        let path = atomicLoad(&gateControl[${GATE_PATH}u]);
        let fullDsu = path == ${GATE_PATH_FULL_VALIDATE}u;

        atomicStore(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], 0u);
        atomicStore(&cacheValidation[${CACHE_VALIDATION_DYNAMIC_CONTACTS}u], 0u);
        atomicStore(&cacheValidation[${CACHE_STAGED_DYNAMIC_CONTACTS}u], 0u);
        atomicStore(&cacheValidation[${CACHE_STAGING_FAILURES}u], 0u);

        fullDsuContactDispatch[0u] = select(0u, contactWorkgroups, fullDsu);
        fullDsuBodyDispatch[0u] = select(0u, bodyWorkgroups, fullDsu);
        unionContactDispatch[0u] = select(0u, contactWorkgroups, fullDsu);
        unionBodyDispatch[0u] = select(0u, bodyWorkgroups, fullDsu);
        serialDsuDispatch[0u] = select(
          0u,
          select(0u, 1u, bodyCount <= ${SERIAL_DSU_BODY_THRESHOLD}u),
          fullDsu,
        );
        unionRoundDispatch[0u] = select(
          0u,
          select(1u, 0u, bodyCount <= ${SERIAL_DSU_BODY_THRESHOLD}u),
          fullDsu,
        );
        fullDsuContactDispatch[1u] = 1u;
        fullDsuContactDispatch[2u] = 1u;
        fullDsuBodyDispatch[1u] = 1u;
        fullDsuBodyDispatch[2u] = 1u;
        unionContactDispatch[1u] = 1u;
        unionContactDispatch[2u] = 1u;
        unionBodyDispatch[1u] = 1u;
        unionBodyDispatch[2u] = 1u;
        serialDsuDispatch[1u] = 1u;
        serialDsuDispatch[2u] = 1u;
        unionRoundDispatch[1u] = 1u;
        unionRoundDispatch[2u] = 1u;

        if (fullDsu) {
          // Invalidate first. The two publication stages after full DSU may
          // overwrite cache data, but VALID cannot become visible until all
          // rows and roots have passed validation.
          atomicStore(&cacheHeader[${CACHE_VALID}u], 0u);
          let stagedVersion = atomicLoad(&cacheHeader[${CACHE_VERSION}u]) + 1u;
          atomicStore(&cacheHeader[${CACHE_VERSION}u], stagedVersion);
          atomicStore(&cacheValidation[${CACHE_STAGED_VERSION}u], stagedVersion);
        } else {
          atomicStore(&cacheValidation[${CACHE_STAGED_VERSION}u], 0u);
        }
      }
    `);
    this.planTopologyDispatchKernel = planTopologyDispatchShader({
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      cacheHeader: storage(this.cacheHeaderAttr, 'uint', CACHE_HEADER_WORDS).toAtomic(),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      fullDsuContactDispatch: storage(this.fullDsuContactDispatchIndirectAttr, 'uint', 3),
      fullDsuBodyDispatch: storage(this.fullDsuBodyDispatchIndirectAttr, 'uint', 3),
      unionContactDispatch: storage(this.unionContactDispatchIndirectAttr, 'uint', 3),
      unionBodyDispatch: storage(this.unionBodyDispatchIndirectAttr, 'uint', 3),
      serialDsuDispatch: storage(this.serialDsuDispatchIndirectAttr, 'uint', 3),
      unionRoundDispatch: storage(this.unionRoundDispatchIndirectAttr, 'uint', 3),
      bodyCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Plan Topology Dispatch');
    assertStorageBufferBudget('Sleeping Islands Plan Topology Dispatch', 10);

    const clearCacheSeenBitsShader = wgslFn(/* wgsl */`
      fn compute(
        rowSeenBits: ptr<storage, array<atomic<u32>>, read_write>,
        pairSeenBits: ptr<storage, array<atomic<u32>>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid < ${Math.max(1, Math.ceil(this.maxPairContacts / 32))}u) {
          atomicStore(&rowSeenBits[gid], 0u);
        }
        if (gid < ${Math.max(1, Math.ceil(this.cachePairHashCapacity / 32))}u) {
          atomicStore(&pairSeenBits[gid], 0u);
        }
      }
    `);
    this.clearCacheSeenBitsKernel = clearCacheSeenBitsShader({
      rowSeenBits: storage(
        this.cacheSeenBitsAttr,
        'uint',
        Math.max(1, Math.ceil(this.maxPairContacts / 32)),
      ).toAtomic(),
      pairSeenBits: storage(
        this.cachePairSeenBitsAttr,
        'uint',
        Math.max(1, Math.ceil(this.cachePairHashCapacity / 32)),
      ).toAtomic(),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Clear Cache Duplicate Bitset');
    assertStorageBufferBudget('Sleeping Islands Clear Cache Duplicate Bitset', 2);

    const validateCachedBodiesShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        cachedBodyRoot: ptr<storage, array<u32>, read>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let pos = positions[gid];
        let dynamicBody = sleepFinite(pos.w) && pos.w > 0.0;
        let cachedRoot = cachedBodyRoot[gid];
        let cachedDynamic = cachedRoot != ${CACHE_STATIC_ROOT}u;
        if (!sleepFinite4(pos) || dynamicBody != cachedDynamic) {
          atomicOr(
            &cacheValidation[${CACHE_VALIDATION_FAILURES}u],
            ${CACHE_FAIL_BODY_CLASS}u,
          );
          return;
        }
        // Wake/fault/static-motion flags change island state, not connectivity.
        // The common cached-root reduction below consumes them in this same
        // command buffer and propagates wake only through the exact cached
        // island. Invalidating topology here would force a global O(N) DSU on
        // every frame of a moving projectile beside an otherwise sleeping 64K
        // scene, even though no dynamic-dynamic contact edge changed.
        if (!dynamicBody) { return; }
        let cachedRootIsExact = cachedRoot < bodyCount
          && cachedBodyRoot[cachedRoot] == cachedRoot
          && positions[cachedRoot].w > 0.0
          && atomicLoad(&parents[gid]) == cachedRoot;
        if (!cachedRootIsExact) {
          atomicOr(
            &cacheValidation[${CACHE_VALIDATION_FAILURES}u],
            ${CACHE_FAIL_ROOT}u,
          );
        }
      }
    `, [helpers]);
    this.validateCachedBodiesKernel = validateCachedBodiesShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      cachedBodyRoot: storage(this.cachedBodyRootAttr, 'uint', this.maxBodies).toReadOnly(),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Validate Cached Body Roots');
    assertStorageBufferBudget('Sleeping Islands Validate Cached Body Roots', 4);

    const validateCachedContactsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        cachedBodyRoot: ptr<storage, array<u32>, read>,
        cachedPairKey: ptr<storage, array<atomic<u32>>, read_write>,
        validationRowSeenBits: ptr<storage, array<atomic<u32>>, read_write>,
        validationPairSeenBits: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${pairActiveContactsOffset}u],
          ${this.maxActivePairContacts}u,
        );
        if (gid >= activeCount) { return; }
        let contact = pairActivity[${pairActiveContactsOffset + 1}u + gid];
        if (contact >= ${this.maxPairContacts}u) {
          atomicOr(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], ${CACHE_FAIL_CONTACT_ROW}u);
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let seenWord = contact >> 5u;
        let seenMask = 1u << (contact & 31u);
        let priorSeen = atomicOr(&validationRowSeenBits[seenWord], seenMask);
        if ((priorSeen & seenMask) != 0u) {
          atomicOr(
            &cacheValidation[${CACHE_VALIDATION_FAILURES}u],
            ${CACHE_FAIL_DUPLICATE_ROW}u,
          );
        }
        let info = pairContacts[
          contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
        ];
        if (!sleepFinite4(info) || info.z < 0.5) {
          atomicOr(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], ${CACHE_FAIL_CONTACT_ROW}u);
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let a = u32(info.x + 0.5);
        let b = u32(info.y + 0.5);
        if (a >= bodyCount || b >= bodyCount || a == b) {
          atomicOr(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let dynamicA = positions[a].w > 0.0;
        let dynamicB = positions[b].w > 0.0;
        if (!dynamicA || !dynamicB) { return; }
        if (a >= 0x10000u || b >= 0x10000u) {
          atomicOr(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
          return;
        }

        // Compact contact rows are allocator-owned and may relocate while the
        // physical graph stays unchanged. Look up the row-independent exact
        // canonical body pair in the immutable published hash table.
        let canonicalA = min(a, b);
        let canonicalB = max(a, b);
        let expectedKey = (canonicalA & 0xffffu) | (canonicalB << 16u);
        var pairHash = expectedKey;
        pairHash ^= pairHash >> 16u;
        pairHash *= 0x7feb352du;
        pairHash ^= pairHash >> 15u;
        pairHash *= 0x846ca68bu;
        pairHash ^= pairHash >> 16u;
        let pairStart = pairHash & ${this.cachePairHashCapacity - 1}u;
        var pairSlot = 0xffffffffu;
        for (var probe = 0u; probe < ${CACHE_PAIR_MAX_PROBES}u; probe++) {
          let slot = (pairStart + probe) & ${this.cachePairHashCapacity - 1}u;
          if (atomicLoad(&cachedPairKey[slot]) == expectedKey) {
            pairSlot = slot;
            break;
          }
        }
        if (pairSlot == 0xffffffffu) {
          atomicOr(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
          return;
        }

        let pairSeenWord = pairSlot >> 5u;
        let pairSeenMask = 1u << (pairSlot & 31u);
        let priorPairSeen = atomicOr(
          &validationPairSeenBits[pairSeenWord],
          pairSeenMask,
        );
        if ((priorPairSeen & pairSeenMask) == 0u) {
          atomicAdd(&cacheValidation[${CACHE_VALIDATION_DYNAMIC_CONTACTS}u], 1u);
        }

        // Membership/count equality proves the exact pair set. Root equality
        // additionally fail-closes corrupted cache or parent state before it
        // can affect support/wake reductions.
        let cachedRootA = cachedBodyRoot[a];
        let cachedRootB = cachedBodyRoot[b];
        if (
          cachedRootA >= bodyCount
          || cachedRootB >= bodyCount
          || cachedRootA != cachedRootB
        ) {
          atomicOr(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
        }
      }
    `, [helpers]);
    this.validateCachedContactsKernel = validateCachedContactsShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      cachedBodyRoot: storage(
        this.cachedBodyRootAttr,
        'uint',
        this.maxBodies,
      ).toReadOnly(),
      cachedPairKey: storage(
        this.cachedPairKeyAttr,
        'uint',
        this.cachePairHashCapacity,
      ).toAtomic(),
      validationRowSeenBits: storage(
        this.cacheSeenBitsAttr,
        'uint',
        Math.max(1, Math.ceil(this.maxPairContacts / 32)),
      ).toAtomic(),
      validationPairSeenBits: storage(
        this.cachePairSeenBitsAttr,
        'uint',
        Math.max(1, Math.ceil(this.cachePairHashCapacity / 32)),
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Validate Cached Contact Topology');
    assertStorageBufferBudget('Sleeping Islands Validate Cached Contact Topology', 9);

    const finalizeCachedTopologyShader = wgslFn(/* wgsl */`
      fn compute(
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        cacheHeader: ptr<storage, array<atomic<u32>>, read_write>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        fullDsuContactDispatch: ptr<storage, array<u32>, read_write>,
        fullDsuBodyDispatch: ptr<storage, array<u32>, read_write>,
        unionContactDispatch: ptr<storage, array<u32>, read_write>,
        unionBodyDispatch: ptr<storage, array<u32>, read_write>,
        serialDsuDispatch: ptr<storage, array<u32>, read_write>,
        unionRoundDispatch: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
      ) -> void {
        if (atomicLoad(&gateControl[${GATE_PATH}u]) != ${GATE_PATH_CACHE_VALIDATE}u) {
          return;
        }
        let bodyWorkgroups = (bodyCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        var contactWorkgroups = 0u;
        // fullDsuContactDispatch is zero during validation. Recover the exact
        // bounded work count from the common contact lane, which was planned
        // from the same immutable canonical list header.
        let dynamicPairs = atomicLoad(
          &cacheValidation[${CACHE_VALIDATION_DYNAMIC_CONTACTS}u]
        );
        var failures = atomicLoad(&cacheValidation[${CACHE_VALIDATION_FAILURES}u]);
        if (
          atomicLoad(&cacheHeader[${CACHE_VALID}u]) == 0u
          || atomicLoad(&cacheHeader[${CACHE_BODY_COUNT}u]) != bodyCount
        ) {
          failures |= ${CACHE_FAIL_HEADER}u;
        }
        if (dynamicPairs != atomicLoad(&cacheHeader[${CACHE_DYNAMIC_CONTACTS}u])) {
          failures |= ${CACHE_FAIL_CONTACT_COUNT}u;
        }
        if ((atomicLoad(&telemetry[${TELEMETRY_FLAGS}u]) & ${FRAME_FAIL_MASK}u) != 0u) {
          failures |= ${CACHE_FAIL_HEADER}u;
        }
        atomicStore(&cacheValidation[${CACHE_VALIDATION_FAILURES}u], failures);
        atomicStore(&telemetry[${TELEMETRY_CACHE_VALIDATION_FAILURES}u], failures);

        if (failures == 0u) {
          atomicStore(&gateControl[${GATE_PATH}u], ${GATE_PATH_CACHED_ROOT}u);
          let hits = atomicAdd(&gateControl[${GATE_CACHED_ROOT_HITS}u], 1u) + 1u;
          atomicStore(&telemetry[${TELEMETRY_CACHED_ROOT_HIT}u], 1u);
          atomicStore(&telemetry[${TELEMETRY_CACHED_ROOT_HITS}u], hits);
          atomicStore(&telemetry[${TELEMETRY_CACHED_ROOT_MISSES}u], atomicLoad(
            &gateControl[${GATE_CACHED_ROOT_MISSES}u]
          ));
          atomicStore(&telemetry[${TELEMETRY_TOPOLOGY_PATH}u], ${GATE_PATH_CACHED_ROOT}u);
          atomicStore(&telemetry[${TELEMETRY_CACHED_DYNAMIC_CONTACTS}u], dynamicPairs);
          return;
        }

        atomicStore(&gateControl[${GATE_PATH}u], ${GATE_PATH_FULL_VALIDATE}u);
        let misses = atomicAdd(&gateControl[${GATE_CACHED_ROOT_MISSES}u], 1u) + 1u;
        let fullCount = atomicAdd(&gateControl[${GATE_FULL_DSU_SUBSTEPS}u], 1u) + 1u;
        atomicStore(&telemetry[${TELEMETRY_CACHED_ROOT_MISS}u], 1u);
        atomicStore(&telemetry[${TELEMETRY_CACHED_ROOT_MISSES}u], misses);
        atomicStore(&telemetry[${TELEMETRY_CACHED_ROOT_HITS}u], atomicLoad(
          &gateControl[${GATE_CACHED_ROOT_HITS}u]
        ));
        atomicStore(&telemetry[${TELEMETRY_FULL_DSU_THIS_SUBSTEP}u], 1u);
        atomicStore(&telemetry[${TELEMETRY_FULL_DSU_SUBSTEPS}u], fullCount);
        atomicStore(&telemetry[${TELEMETRY_TOPOLOGY_PATH}u], ${GATE_PATH_FULL_VALIDATE}u);

        // Validation contact workgroups are identical to full DSU contact
        // workgroups. They are copied by the CPU-independent planner through
        // the telemetry active count here.
        let activeCount = atomicLoad(&telemetry[${TELEMETRY_ACTIVE_CONTACTS}u]);
        contactWorkgroups = (activeCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        fullDsuContactDispatch[0u] = contactWorkgroups;
        fullDsuBodyDispatch[0u] = bodyWorkgroups;
        unionContactDispatch[0u] = contactWorkgroups;
        unionBodyDispatch[0u] = bodyWorkgroups;
        serialDsuDispatch[0u] = select(0u, 1u, bodyCount <= ${SERIAL_DSU_BODY_THRESHOLD}u);
        unionRoundDispatch[0u] = select(1u, 0u, bodyCount <= ${SERIAL_DSU_BODY_THRESHOLD}u);

        atomicStore(&cacheHeader[${CACHE_VALID}u], 0u);
        let stagedVersion = atomicLoad(&cacheHeader[${CACHE_VERSION}u]) + 1u;
        atomicStore(&cacheHeader[${CACHE_VERSION}u], stagedVersion);
        atomicStore(&cacheValidation[${CACHE_STAGED_VERSION}u], stagedVersion);
      }
    `);
    this.finalizeCachedTopologyKernel = finalizeCachedTopologyShader({
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      cacheHeader: storage(this.cacheHeaderAttr, 'uint', CACHE_HEADER_WORDS).toAtomic(),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      fullDsuContactDispatch: storage(this.fullDsuContactDispatchIndirectAttr, 'uint', 3),
      fullDsuBodyDispatch: storage(this.fullDsuBodyDispatchIndirectAttr, 'uint', 3),
      unionContactDispatch: storage(this.unionContactDispatchIndirectAttr, 'uint', 3),
      unionBodyDispatch: storage(this.unionBodyDispatchIndirectAttr, 'uint', 3),
      serialDsuDispatch: storage(this.serialDsuDispatchIndirectAttr, 'uint', 3),
      unionRoundDispatch: storage(this.unionRoundDispatchIndirectAttr, 'uint', 3),
      bodyCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Resolve Cached Topology');
    assertStorageBufferBudget('Sleeping Islands Resolve Cached Topology', 10);

    const planCacheStagingSeenClearShader = wgslFn(/* wgsl */`
      fn compute(
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        clearDispatch: ptr<storage, array<u32>, read_write>,
      ) -> void {
        let fullDsu = atomicLoad(&gateControl[${GATE_PATH}u])
          == ${GATE_PATH_FULL_VALIDATE}u;
        clearDispatch[0u] = select(
          0u,
          (${Math.max(
            this.cachePairHashCapacity,
            Math.max(1, Math.ceil(this.maxPairContacts / 32)),
          )}u
            + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u,
          fullDsu,
        );
        clearDispatch[1u] = 1u;
        clearDispatch[2u] = 1u;
      }
    `);
    this.planCacheStagingSeenClearKernel = planCacheStagingSeenClearShader({
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      clearDispatch: storage(this.cacheStagingSeenClearDispatchIndirectAttr, 'uint', 3),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Plan Cache Staging Seen Clear');
    assertStorageBufferBudget('Sleeping Islands Plan Cache Staging Seen Clear', 2);

    const clearCacheStagingShader = wgslFn(/* wgsl */`
      fn compute(
        cachedPairKey: ptr<storage, array<atomic<u32>>, read_write>,
        rowSeenBits: ptr<storage, array<atomic<u32>>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid < ${this.cachePairHashCapacity}u) {
          atomicStore(&cachedPairKey[gid], 0u);
        }
        if (gid < ${Math.max(1, Math.ceil(this.maxPairContacts / 32))}u) {
          atomicStore(&rowSeenBits[gid], 0u);
        }
      }
    `);
    this.clearCacheStagingKernel = clearCacheStagingShader({
      cachedPairKey: storage(
        this.cachedPairKeyAttr,
        'uint',
        this.cachePairHashCapacity,
      ).toAtomic(),
      rowSeenBits: storage(
        this.cacheSeenBitsAttr,
        'uint',
        Math.max(1, Math.ceil(this.maxPairContacts / 32)),
      ).toAtomic(),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Clear Cache Staging');
    assertStorageBufferBudget('Sleeping Islands Clear Cache Staging', 2);

    const stageCachedBodiesShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        cachedBodyRoot: ptr<storage, array<u32>, read_write>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let pos = positions[gid];
        if (!sleepFinite4(pos)) {
          cachedBodyRoot[gid] = ${CACHE_STATIC_ROOT}u;
          atomicOr(
            &cacheValidation[${CACHE_STAGING_FAILURES}u],
            ${CACHE_FAIL_BODY_CLASS}u,
          );
          return;
        }
        if (pos.w <= 0.0) {
          cachedBodyRoot[gid] = ${CACHE_STATIC_ROOT}u;
          return;
        }
        let root = sleepFindRoot(parents, gid, bodyCount);
        if (root == 0xffffffffu || root >= bodyCount || positions[root].w <= 0.0) {
          cachedBodyRoot[gid] = ${CACHE_STATIC_ROOT}u;
          atomicOr(
            &cacheValidation[${CACHE_STAGING_FAILURES}u],
            ${CACHE_FAIL_ROOT}u,
          );
          return;
        }
        cachedBodyRoot[gid] = root;
      }
    `, [helpers]);
    this.stageCachedBodiesKernel = stageCachedBodiesShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      cachedBodyRoot: storage(this.cachedBodyRootAttr, 'uint', this.maxBodies),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Stage Cached Body Roots');
    assertStorageBufferBudget('Sleeping Islands Stage Cached Body Roots', 4);

    const stageCachedContactsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        cachedBodyRoot: ptr<storage, array<u32>, read>,
        cachedPairKey: ptr<storage, array<atomic<u32>>, read_write>,
        validationSeenBits: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${pairActiveContactsOffset}u],
          ${this.maxActivePairContacts}u,
        );
        if (gid >= activeCount) { return; }
        let contact = pairActivity[${pairActiveContactsOffset + 1}u + gid];
        if (contact >= ${this.maxPairContacts}u) {
          atomicOr(&cacheValidation[${CACHE_STAGING_FAILURES}u], ${CACHE_FAIL_CONTACT_ROW}u);
          return;
        }
        let seenWord = contact >> 5u;
        let seenMask = 1u << (contact & 31u);
        let priorSeen = atomicOr(&validationSeenBits[seenWord], seenMask);
        if ((priorSeen & seenMask) != 0u) {
          atomicOr(
            &cacheValidation[${CACHE_STAGING_FAILURES}u],
            ${CACHE_FAIL_DUPLICATE_ROW}u,
          );
        }
        let info = pairContacts[
          contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
        ];
        if (!sleepFinite4(info) || info.z < 0.5) {
          atomicOr(&cacheValidation[${CACHE_STAGING_FAILURES}u], ${CACHE_FAIL_CONTACT_ROW}u);
          return;
        }
        let a = u32(info.x + 0.5);
        let b = u32(info.y + 0.5);
        if (a >= bodyCount || b >= bodyCount || a == b) {
          atomicOr(&cacheValidation[${CACHE_STAGING_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
          return;
        }
        if (positions[a].w <= 0.0 || positions[b].w <= 0.0) {
          return;
        }
        if (a >= 0x10000u || b >= 0x10000u) {
          atomicOr(&cacheValidation[${CACHE_STAGING_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
          return;
        }
        let cachedRootA = cachedBodyRoot[a];
        let cachedRootB = cachedBodyRoot[b];
        if (
          cachedRootA >= bodyCount
          || cachedRootB >= bodyCount
          || cachedRootA != cachedRootB
        ) {
          atomicOr(&cacheValidation[${CACHE_STAGING_FAILURES}u], ${CACHE_FAIL_ROOT}u);
          return;
        }

        let canonicalA = min(a, b);
        let canonicalB = max(a, b);
        let expectedKey = (canonicalA & 0xffffu) | (canonicalB << 16u);
        var pairHash = expectedKey;
        pairHash ^= pairHash >> 16u;
        pairHash *= 0x7feb352du;
        pairHash ^= pairHash >> 15u;
        pairHash *= 0x846ca68bu;
        pairHash ^= pairHash >> 16u;
        let pairStart = pairHash & ${this.cachePairHashCapacity - 1}u;
        for (var probe = 0u; probe < ${CACHE_PAIR_MAX_PROBES}u; probe++) {
          let slot = (pairStart + probe) & ${this.cachePairHashCapacity - 1}u;
          var owner = atomicLoad(&cachedPairKey[slot]);
          if (owner == expectedKey) { return; }
          if (owner == 0u) {
            var installed = false;
            for (var attempt = 0u; attempt < 16u; attempt++) {
              let claimed = atomicCompareExchangeWeak(
                &cachedPairKey[slot],
                0u,
                expectedKey,
              );
              owner = claimed.old_value;
              if (claimed.exchanged) {
                installed = true;
                break;
              }
              if (owner != 0u) { break; }
            }
            if (installed) {
              atomicAdd(&cacheValidation[${CACHE_STAGED_DYNAMIC_CONTACTS}u], 1u);
              return;
            }
            if (owner == expectedKey) { return; }
            // Repeated weak-CAS spurious failure is not permission to skip an
            // empty slot: doing so could install the same key twice. Decline
            // cache publication instead.
            if (owner == 0u) {
              atomicOr(
                &cacheValidation[${CACHE_STAGING_FAILURES}u],
                ${CACHE_FAIL_CONTACT_PAIR}u,
              );
              return;
            }
          }
        }
        // Probe exhaustion is a cache-capacity/performance failure only. The
        // full DSU result remains valid, but this cache generation is not
        // published.
        atomicOr(&cacheValidation[${CACHE_STAGING_FAILURES}u], ${CACHE_FAIL_CONTACT_PAIR}u);
      }
    `, [helpers]);
    this.stageCachedContactsKernel = stageCachedContactsShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      cachedBodyRoot: storage(
        this.cachedBodyRootAttr,
        'uint',
        this.maxBodies,
      ).toReadOnly(),
      cachedPairKey: storage(
        this.cachedPairKeyAttr,
        'uint',
        this.cachePairHashCapacity,
      ).toAtomic(),
      validationSeenBits: storage(
        this.cacheSeenBitsAttr,
        'uint',
        Math.max(1, Math.ceil(this.maxPairContacts / 32)),
      ).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Stage Cached Contact Topology');
    assertStorageBufferBudget('Sleeping Islands Stage Cached Contact Topology', 7);

    const publishCachedTopologyShader = wgslFn(/* wgsl */`
      fn compute(
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        cacheHeader: ptr<storage, array<atomic<u32>>, read_write>,
        cacheValidation: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        topologyKnown: u32,
        topologyGeneration: u32,
      ) -> void {
        if (atomicLoad(&gateControl[${GATE_PATH}u]) != ${GATE_PATH_FULL_VALIDATE}u) {
          return;
        }
        var failures = atomicLoad(&cacheValidation[${CACHE_STAGING_FAILURES}u]);
        if ((atomicLoad(&telemetry[${TELEMETRY_FLAGS}u]) & ${FRAME_FAIL_MASK}u) != 0u) {
          failures |= ${CACHE_FAIL_HEADER}u;
        }
        let dynamicContacts = atomicLoad(
          &cacheValidation[${CACHE_STAGED_DYNAMIC_CONTACTS}u]
        );
        if (
          topologyKnown == 0u
          || atomicLoad(&cacheHeader[${CACHE_DISABLED}u]) != 0u
        ) {
          failures |= ${CACHE_FAIL_HEADER}u;
        }
        if (failures != 0u) {
          atomicStore(&cacheHeader[${CACHE_VALID}u], 0u);
          atomicOr(&telemetry[${TELEMETRY_CACHE_VALIDATION_FAILURES}u], failures);
          return;
        }

        // Publication stage two: metadata first, VALID last. A later cache
        // validator can therefore observe either the complete old cache or
        // the complete new cache, never a partially staged root/contact set.
        atomicStore(&cacheHeader[${CACHE_BODY_COUNT}u], bodyCount);
        atomicStore(&cacheHeader[${CACHE_TOPOLOGY_GENERATION}u], topologyGeneration);
        atomicStore(&cacheHeader[${CACHE_DYNAMIC_CONTACTS}u], dynamicContacts);
        atomicStore(
          &cacheHeader[${CACHE_PUBLISHED_EPOCH}u],
          atomicLoad(&gateControl[${GATE_SUBSTEP_EPOCH}u]),
        );
        atomicStore(&cacheHeader[${CACHE_VALID}u], 1u);
        atomicStore(&telemetry[${TELEMETRY_CACHED_DYNAMIC_CONTACTS}u], dynamicContacts);
      }
    `);
    this.publishCachedTopologyKernel = publishCachedTopologyShader({
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      cacheHeader: storage(this.cacheHeaderAttr, 'uint', CACHE_HEADER_WORDS).toAtomic(),
      cacheValidation: storage(
        this.cacheValidationAttr,
        'uint',
        CACHE_VALIDATION_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      topologyKnown: uniform(0),
      topologyGeneration: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Publish Cached Topology');
    assertStorageBufferBudget('Sleeping Islands Publish Cached Topology', 4);

    const fastGateCleanupShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read_write>,
        stableFrames: ptr<storage, array<u32>, read_write>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        poseHistory: ptr<storage, array<vec4f>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let pos = positions[gid];
        let historyBase = gid * ${BODY_HISTORY_VEC4S}u;
        let state = bodyState[gid];
        if (
          pos.w > 0.0
          && (state & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u
        ) {
          atomicAdd(&telemetry[${TELEMETRY_WAKE_EVENTS}u], 1u);
        }
        bodyState[gid] = ${BODY_INITIALIZED}u | (state & ${BODY_CONSERVATIVE}u);
        stableFrames[gid] = 0u;
        bodyConstraintCounts[gid] &= ~${BODY_SLEEP_FLAG}u;
        poseHistory[historyBase] = pos;
        poseHistory[historyBase + 1u] = quaternions[gid];
      }
    `);
    this.fastGateCleanupKernel = fastGateCleanupShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies),
      stableFrames: storage(this.stableFramesAttr, 'uint', this.maxBodies),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', this.maxBodies),
      poseHistory: storage(
        this.poseHistoryAttr,
        'vec4f',
        this.maxBodies * BODY_HISTORY_VEC4S,
      ),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Active Fast-Gate Cleanup');
    assertStorageBufferBudget('Sleeping Islands Active Fast-Gate Cleanup', 7);

    const serialDsuShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
      ) -> void {
        for (var body = 0u; body < bodyCount; body++) {
          atomicStore(&parents[body], body);
        }

        let activeCount = min(
          pairActivity[${pairActiveContactsOffset}u],
          ${this.maxActivePairContacts}u,
        );
        for (var gid = 0u; gid < activeCount; gid++) {
          let contact = pairActivity[${pairActiveContactsOffset + 1}u + gid];
          if (contact >= ${this.maxPairContacts}u) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
            atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
            continue;
          }
          let info = pairContacts[
            contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
          ];
          if (!sleepFinite4(info) || info.z < 0.5) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
            atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
            continue;
          }
          let a = u32(info.x + 0.5);
          let b = u32(info.y + 0.5);
          if (a >= bodyCount || b >= bodyCount || a == b) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
            atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
            continue;
          }
          if (positions[a].w <= 0.0 || positions[b].w <= 0.0) { continue; }

          let rootA = sleepSerialFindRoot(parents, a, bodyCount);
          let rootB = sleepSerialFindRoot(parents, b, bodyCount);
          if (rootA == 0xffffffffu || rootB == 0xffffffffu) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
            atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
            continue;
          }
          if (rootA != rootB) {
            let high = max(rootA, rootB);
            let low = min(rootA, rootB);
            atomicStore(&parents[high], low);
          }
        }

        // Flatten every label before parallel island reduction. The generated
        // loop bound matches the serial selection threshold, so it covers even
        // an adversarial linear tree at the 1,024-body boundary.
        for (var body = 0u; body < bodyCount; body++) {
          let root = sleepSerialFindRoot(parents, body, bodyCount);
          if (root == 0xffffffffu) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
            atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
          } else {
            atomicStore(&parents[body], root);
          }
        }
      }
    `, [helpers]);
    this.serialDsuKernel = serialDsuShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Serial Small-Graph DSU');
    assertStorageBufferBudget('Sleeping Islands Serial Small-Graph DSU', 5);

    const unionContactsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        unionChanged: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${pairActiveContactsOffset}u],
          ${this.maxActivePairContacts}u,
        );
        if (gid >= activeCount) { return; }
        let contact = pairActivity[${pairActiveContactsOffset + 1}u + gid];
        if (contact >= ${this.maxPairContacts}u) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let info = pairContacts[
          contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
        ];
        if (!sleepFinite4(info) || info.z < 0.5) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let a = u32(info.x + 0.5);
        let b = u32(info.y + 0.5);
        if (a >= bodyCount || b >= bodyCount || a == b) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        if (positions[a].w <= 0.0 || positions[b].w <= 0.0) { return; }

        // Shiloach-Vishkin-style monotone hooking: labels only decrease, so
        // the forest is acyclic. atomicMin has no weak-CAS spurious-failure
        // case, which makes a zero changed flag a valid convergence signal.
        let labelA = atomicLoad(&parents[a]);
        let labelB = atomicLoad(&parents[b]);
        if (labelA >= bodyCount || labelB >= bodyCount) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
          return;
        }
        if (labelA == labelB) { return; }
        let high = max(labelA, labelB);
        let low = min(labelA, labelB);
        let previous = atomicMin(&parents[high], low);
        if (previous > low) {
          atomicStore(&unionChanged[0u], 1u);
        }
      }
    `, [helpers]);
    this.unionContactsKernel = unionContactsShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      unionChanged: storage(this.unionChangedAttr, 'uint', 1).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Union Contacts');
    assertStorageBufferBudget('Sleeping Islands Union Contacts', 6);

    const pointerJumpShader = wgslFn(/* wgsl */`
      fn compute(
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        unionChanged: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let parent = atomicLoad(&parents[gid]);
        if (parent >= bodyCount) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
          return;
        }
        let grandParent = atomicLoad(&parents[parent]);
        if (grandParent >= bodyCount || grandParent > parent) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
          return;
        }
        if (grandParent < parent) {
          let previous = atomicMin(&parents[gid], grandParent);
          if (previous > grandParent) {
            atomicStore(&unionChanged[0u], 1u);
          }
        }
      }
    `);
    this.pointerJumpKernel = pointerJumpShader({
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      unionChanged: storage(this.unionChangedAttr, 'uint', 1).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Pointer Jump');
    assertStorageBufferBudget('Sleeping Islands Pointer Jump', 3);

    const finishUnionRoundShader = wgslFn(/* wgsl */`
      fn compute(
        unionChanged: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        unionContactDispatchIndirect: ptr<storage, array<u32>, read_write>,
        unionBodyDispatchIndirect: ptr<storage, array<u32>, read_write>,
        nextUnionRoundDispatchIndirect: ptr<storage, array<u32>, read_write>,
      ) -> void {
        // This is both the previous round's convergence test and the next
        // round's clear. atomicExchange preserves the old boolean exactly
        // while making the next union/pointer pair start from zero.
        let changed = atomicExchange(&unionChanged[0u], 0u);
        let completed = atomicAdd(
          &telemetry[${TELEMETRY_DSU_ROUNDS_EXECUTED}u],
          1u,
        ) + 1u;
        let keepRunning = select(0u, 1u, changed != 0u);
        nextUnionRoundDispatchIndirect[0u] = keepRunning;
        nextUnionRoundDispatchIndirect[1u] = 1u;
        nextUnionRoundDispatchIndirect[2u] = 1u;
        if (changed == 0u) {
          unionContactDispatchIndirect[0u] = 0u;
          unionBodyDispatchIndirect[0u] = 0u;
          // The old graph continued to execute one clear and one finalizer in
          // every unused bounded slot. Clears for all slots were already
          // counted by initializeDsu; add the finalizers skipped after this
          // converged round. If convergence consumes the full budget, the
          // addition is zero and the worst-case iteration count is unchanged.
          let remaining = ${this.unionPasses}u - min(completed, ${this.unionPasses}u);
          atomicAdd(&telemetry[${TELEMETRY_DSU_DISPATCHES_SAVED}u], remaining);
        }
      }
    `);
    this.finishUnionRoundKernels = Object.freeze(Array.from(
      { length: this.unionPasses },
      (_, round) => finishUnionRoundShader({
        unionChanged: storage(this.unionChangedAttr, 'uint', 1).toAtomic(),
        telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
        unionContactDispatchIndirect: storage(
          this.unionContactDispatchIndirectAttr,
          'uint',
          3,
        ),
        unionBodyDispatchIndirect: storage(this.unionBodyDispatchIndirectAttr, 'uint', 3),
        nextUnionRoundDispatchIndirect: storage(
          this.unionRoundDispatchIndirectAttrs[round + 1]!,
          'uint',
          3,
        ),
      }).computeKernel([1, 1, 1]).setName(
        `Sleeping Islands Finish Union Round ${round + 1}`,
      ),
    ));
    assertStorageBufferBudget('Sleeping Islands Finish Union Round', 4);

    // Continuation arguments are intentionally one-shot. Without this bounded
    // epilogue, arguments consumed before convergence would remain one and a
    // later cached-root frame could execute stale finalizers even though its
    // first round is disabled. Ten writable bindings match the requested
    // per-stage limit, so a 64-round diagnostic ceiling needs at most seven
    // one-invocation cleanup dispatches; production budgets need two.
    const unionRoundContinuations = this.unionRoundDispatchIndirectAttrs.slice(1);
    const continuationClearKernels: any[] = [];
    for (
      let start = 0;
      start < unionRoundContinuations.length;
      start += DSU_CONTINUATION_CLEAR_BINDINGS
    ) {
      const attributes = unionRoundContinuations.slice(
        start,
        start + DSU_CONTINUATION_CLEAR_BINDINGS,
      );
      const parameterSource = attributes.map((_, index) => (
        `dispatch${index}: ptr<storage, array<u32>, read_write>`
      )).join(',\n        ');
      const clearSource = attributes.map((_, index) => (
        `dispatch${index}[0u] = 0u;`
      )).join('\n        ');
      const clearContinuationShader = wgslFn(/* wgsl */`
        fn compute(
          ${parameterSource},
        ) -> void {
          ${clearSource}
        }
      `);
      const bindings: Record<string, any> = {};
      for (let index = 0; index < attributes.length; index++) {
        bindings[`dispatch${index}`] = storage(attributes[index]!, 'uint', 3);
      }
      continuationClearKernels.push(clearContinuationShader(bindings)
        .computeKernel([1, 1, 1])
        .setName(`Sleeping Islands Clear Union Continuations ${start + 1}`));
      assertStorageBufferBudget(
        'Sleeping Islands Clear Union Continuations',
        attributes.length,
      );
    }
    this.clearUnionRoundContinuationKernels = Object.freeze(continuationClearKernels);

    const compressParentsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let root = sleepFindRoot(parents, gid, bodyCount);
        if (root == 0xffffffffu) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
          return;
        }
        atomicStore(&parents[gid], root);
      }
    `, [helpers]);
    this.compressParentsKernel = compressParentsShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Compress Parents');
    assertStorageBufferBudget('Sleeping Islands Compress Parents', 3);

    const clearIslandsShader = wgslFn(/* wgsl */`
      fn compute(
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        preserveContactState: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let base = gid * ${ISLAND_WORDS}u;
        let previousFlags = atomicLoad(&islandData[base]);
        let preservedFlags = previousFlags & ${ISLAND_SUPPORTED | ISLAND_CONTACT_INELIGIBLE}u;
        atomicStore(&islandData[base], select(0u, preservedFlags, preserveContactState != 0u));
        atomicStore(&islandData[base + 1u], 0u);
        atomicStore(&islandData[base + 2u], 0xffffffffu);
        atomicStore(&islandData[base + 3u], 0u);
      }
    `);
    this.clearIslandsKernel = clearIslandsShader({
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      preserveContactState: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Clear Aggregates');
    assertStorageBufferBudget('Sleeping Islands Clear Aggregates', 1);
    // Use a distinct compute node for the post-solve clear. This lets callers
    // concatenate buildBeforeSolveComputeNodes() and
    // buildAfterSolveComputeNodes() into one encoder without the post phase
    // mutating the pre phase's uniform object before submission.
    this.clearIslandsAfterKernel = clearIslandsShader({
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      preserveContactState: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Clear Post-Solve Aggregates');
    assertStorageBufferBudget('Sleeping Islands Clear Post-Solve Aggregates', 1);

    const reduceBodiesBeforeShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let root = sleepFindRoot(parents, gid, bodyCount);
        if (root == 0xffffffffu) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
          return;
        }
        let state = bodyState[gid];
        var flags = 0u;
        if ((state & ${BODY_LOCAL_WAKE | BODY_CONSERVATIVE}u) != 0u) {
          flags |= ${ISLAND_WAKE | ISLAND_INELIGIBLE}u;
        }
        if ((state & ${BODY_LOCAL_FAULT}u) != 0u) {
          flags |= ${ISLAND_FAULT | ISLAND_INELIGIBLE}u;
        }
        if ((state & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u) {
          flags |= ${ISLAND_HAS_SLEEPING}u;
          atomicAdd(&islandData[root * ${ISLAND_WORDS}u + 3u], 1u);
        } else {
          flags |= ${ISLAND_HAS_AWAKE}u;
        }
        atomicOr(&islandData[root * ${ISLAND_WORDS}u], flags);
        atomicAdd(&islandData[root * ${ISLAND_WORDS}u + 1u], 1u);
      }
    `, [helpers]);
    this.reduceBodiesBeforeKernel = reduceBodiesBeforeShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reduce Bodies Before Solve');
    assertStorageBufferBudget('Sleeping Islands Reduce Bodies Before Solve', 5);

    const reduceContactsBeforeShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        supportDirection: vec3f,
        supportNormalCosine: f32,
        wakeLinearSpeed: f32,
        sleepLinearSpeed: f32,
        wakeContactPenetration: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(pairActivity[${pairActiveContactsOffset}u], ${this.maxActivePairContacts}u);
        if (gid >= activeCount) { return; }
        let contact = pairActivity[${pairActiveContactsOffset + 1}u + gid];
        if (contact >= ${this.maxPairContacts}u) { return; }
        let base = contact * ${CONTACT_RECORD_VEC4S}u;
        let info = pairContacts[base + ${CONTACT_RECORD_META_OFFSET}u];
        let normalPen = pairContacts[base + ${CONTACT_RECORD_NORMAL_PEN_OFFSET}u];
        if (!sleepFinite4(info) || !sleepFinite4(normalPen) || info.z < 0.5) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let a = u32(info.x + 0.5);
        let b = u32(info.y + 0.5);
        if (a >= bodyCount || b >= bodyCount || a == b) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let dynamicA = positions[a].w > 0.0;
        let dynamicB = positions[b].w > 0.0;
        if (!dynamicA && !dynamicB) { return; }
        let n2 = dot(normalPen.xyz, normalPen.xyz);
        if (!sleepFinite(n2) || n2 <= 1e-12) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          return;
        }
        let n = normalPen.xyz * inverseSqrt(n2);
        var root = select(a, b, dynamicB);
        if (dynamicA) { root = sleepFindRoot(parents, a, bodyCount); }
        if (root == 0xffffffffu) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          return;
        }

        if (dynamicA && dynamicB) {
          let rootB = sleepFindRoot(parents, b, bodyCount);
          if (rootB != root || rootB == 0xffffffffu) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
            atomicAdd(&telemetry[${TELEMETRY_UNION_FAILURES}u], 1u);
            atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_FAULT | ISLAND_WAKE}u);
            return;
          }
        } else {
          let supportDot = select(dot(-n, supportDirection), dot(n, supportDirection), dynamicB);
          if (dot(supportDirection, supportDirection) > 0.5 && supportDot >= supportNormalCosine) {
            atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_SUPPORTED}u);
          }
          let staticBody = select(b, a, dynamicB);
          if ((bodyState[staticBody] & ${BODY_STATIC_MOVED}u) != 0u) {
            atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_WAKE}u);
          }
        }

        let relativeNormalSpeed = abs(dot(velocities[b].xyz - velocities[a].xyz, n));
        if (
          relativeNormalSpeed > wakeLinearSpeed
          || normalPen.w > wakeContactPenetration
        ) {
          atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_WAKE}u);
        }
        // Absolute penetration is not a convergence residual in AVBD: a
        // supported compliant contact can carry a stable, non-zero overlap
        // under load.  Using that overlap as a sleep veto made otherwise
        // motionless stacks depend on the last contact-color ordering.  Gate
        // dwell on *contact motion* instead; wakeContactPenetration remains
        // the fail-safe for a genuinely excessive overlap.
        if (relativeNormalSpeed > sleepLinearSpeed) {
          atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_CONTACT_INELIGIBLE}u);
          atomicAdd(&telemetry[${TELEMETRY_INELIGIBLE_CONTACTS}u], 1u);
        }
        if (normalPen.w >= 0.0) {
          atomicMax(
            &telemetry[${TELEMETRY_MAX_CONTACT_PENETRATION_BITS}u],
            bitcast<u32>(normalPen.w),
          );
        }
      }
    `, [helpers]);
    this.reduceContactsBeforeKernel = reduceContactsBeforeShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      supportDirection: uniform(supportDirection),
      supportNormalCosine: uniform(supportNormalCosine),
      wakeLinearSpeed: uniform(wakeLinear),
      sleepLinearSpeed: uniform(sleepLinear),
      wakeContactPenetration: uniform(wakeContactPenetration),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reduce Contacts Before Solve');
    assertStorageBufferBudget('Sleeping Islands Reduce Contacts Before Solve', 8);

    const applyWakeShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        bodyState: ptr<storage, array<u32>, read_write>,
        stableFrames: ptr<storage, array<u32>, read_write>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        var countWord = bodyConstraintCounts[gid] & ~${BODY_SLEEP_FLAG}u;
        if (positions[gid].w <= 0.0) {
          bodyConstraintCounts[gid] = countWord;
          return;
        }
        let root = sleepFindRoot(parents, gid, bodyCount);
        let globalFlags = atomicLoad(&telemetry[${TELEMETRY_FLAGS}u]);
        var wake = root == 0xffffffffu || (globalFlags & ${FRAME_FAIL_MASK}u) != 0u;
        var islandFlags = ${ISLAND_FAULT | ISLAND_WAKE}u;
        if (root != 0xffffffffu) {
          islandFlags = atomicLoad(&islandData[root * ${ISLAND_WORDS}u]);
        }
        let wasSleeping = (bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u;
        wake = wake || (islandFlags & ${ISLAND_FAULT | ISLAND_WAKE | ISLAND_INELIGIBLE}u) != 0u;
        wake = wake || (
          (islandFlags & ${ISLAND_HAS_SLEEPING | ISLAND_HAS_AWAKE}u)
          == ${ISLAND_HAS_SLEEPING | ISLAND_HAS_AWAKE}u
        );
        if (wasSleeping && (islandFlags & ${ISLAND_SUPPORTED}u) == 0u) {
          wake = true;
        }
        // Keep the conservative marker through the after-solve eligibility
        // pass; applySleep removes transient bits after consuming it.
        var state = bodyState[gid]
          & ${BODY_INITIALIZED | SLEEPING_BODY_STATE_SLEEPING_BIT | BODY_CONSERVATIVE}u;
        if (wake) {
          state &= ~${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
          stableFrames[gid] = 0u;
          if (wasSleeping) {
            atomicAdd(&telemetry[${TELEMETRY_WAKE_EVENTS}u], 1u);
          }
        } else if (wasSleeping) {
          velocities[gid] = vec4f(0.0);
          angularVelocities[gid] = vec4f(0.0);
          countWord |= ${BODY_SLEEP_FLAG}u;
          // This is the post-wake, pre-solve sleeping population. A zero
          // count proves every dynamic endpoint is awake, so the solver can
          // alias the immutable canonical contact list instead of issuing an
          // O(active contacts) scratch compaction pass.
          atomicAdd(
            &telemetry[${TELEMETRY_PRE_SOLVE_SLEEPING_BODIES}u],
            1u,
          );
        }
        bodyState[gid] = state;
        bodyConstraintCounts[gid] = countWord;
      }
    `, [helpers]);
    this.applyWakeKernel = applyWakeShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', this.maxBodies),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies),
      stableFrames: storage(this.stableFramesAttr, 'uint', this.maxBodies),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', this.maxBodies),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Apply Wake');
    assertStorageBufferBudget('Sleeping Islands Apply Wake', 9);

    const freezeSleepingShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        bodyState: ptr<storage, array<u32>, read>,
        initialPose: ptr<storage, array<vec4f>, read_write>,
        inertialPose: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (
          gid >= bodyCount
          || (bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) == 0u
        ) { return; }
        let pos = positions[gid];
        let q = quaternions[gid];
        velocities[gid] = vec4f(0.0);
        angularVelocities[gid] = vec4f(0.0);
        let initialBase = gid * 2u;
        initialPose[initialBase] = pos;
        initialPose[initialBase + 1u] = q;
        let inertialBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        inertialPose[inertialBase] = pos;
        inertialPose[inertialBase + 1u] = q;
        inertialPose[inertialBase + 2u] = pos;
        inertialPose[inertialBase + 3u] = q;
        inertialPose[inertialBase + 4u] = vec4f(0.0);
      }
    `);
    this.freezeSleepingKernel = freezeSleepingShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', this.maxBodies),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      initialPose: storage(initialPose, 'vec4f', this.maxBodies * 2),
      inertialPose: storage(
        inertialPose,
        'vec4f',
        this.maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
      ),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Freeze AVBD State');
    assertStorageBufferBudget('Sleeping Islands Freeze AVBD State', 7);

    const reduceBodiesAfterShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        shapes: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        poseHistory: ptr<storage, array<vec4f>, read>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        sleepLinearSpeed: f32,
        sleepAngularSpeed: f32,
        sleepPositionCorrection: f32,
        sleepAngularCorrection: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let root = sleepFindRoot(parents, gid, bodyCount);
        if (root == 0xffffffffu) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          return;
        }
        let pos = positions[gid];
        let shape = shapes[gid];
        let q = quaternions[gid];
        let v = velocities[gid];
        let w = angularVelocities[gid];
        let prePos = poseHistory[gid * ${BODY_HISTORY_VEC4S}u + 2u];
        let preQ = poseHistory[gid * ${BODY_HISTORY_VEC4S}u + 3u];
        let boundingRadius = sleepBoundingRadius(shape);
        let speed2 = dot(v.xyz, v.xyz);
        let angularSurfaceSpeed2 = dot(w.xyz, w.xyz) * boundingRadius * boundingRadius;
        let correction = pos.xyz - prePos.xyz;
        let correction2 = dot(correction, correction);
        let angularSurfaceCorrection = sleepQuaternionDelta(q, preQ) * boundingRadius;
        let valid = sleepFinite4(pos) && sleepShapeValid(shape) && sleepFinite4(q)
          && sleepFinite4(v) && sleepFinite4(w)
          && sleepFinite4(prePos) && sleepFinite4(preQ)
          && sleepFinite(boundingRadius) && boundingRadius >= 0.0
          && sleepFinite(speed2) && sleepFinite(angularSurfaceSpeed2)
          && sleepFinite(correction2) && sleepFinite(angularSurfaceCorrection);
        var eligible = valid
          && (bodyState[gid] & ${BODY_CONSERVATIVE}u) == 0u
          && speed2 <= sleepLinearSpeed * sleepLinearSpeed
          && angularSurfaceSpeed2 <= sleepAngularSpeed * sleepAngularSpeed
          && correction2 <= sleepPositionCorrection * sleepPositionCorrection
          && angularSurfaceCorrection <= sleepAngularCorrection;
        if (
          valid
          && (
            speed2 > sleepLinearSpeed * sleepLinearSpeed
            || angularSurfaceSpeed2 > sleepAngularSpeed * sleepAngularSpeed
          )
        ) {
          atomicAdd(&telemetry[${TELEMETRY_INELIGIBLE_MOTION_BODIES}u], 1u);
        }
        if (
          valid
          && (
            correction2 > sleepPositionCorrection * sleepPositionCorrection
            || angularSurfaceCorrection > sleepAngularCorrection
          )
        ) {
          atomicAdd(&telemetry[${TELEMETRY_INELIGIBLE_CORRECTION_BODIES}u], 1u);
        }
        if (!valid) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          atomicAdd(&telemetry[${TELEMETRY_INVALID_INPUTS}u], 1u);
          eligible = false;
        }
        var flags = select(${ISLAND_INELIGIBLE}u, 0u, eligible);
        if ((bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u) {
          flags |= ${ISLAND_HAS_SLEEPING}u;
          atomicAdd(&islandData[root * ${ISLAND_WORDS}u + 3u], 1u);
        } else {
          flags |= ${ISLAND_HAS_AWAKE}u;
        }
        atomicOr(&islandData[root * ${ISLAND_WORDS}u], flags);
        atomicAdd(&islandData[root * ${ISLAND_WORDS}u + 1u], 1u);
        if (valid) {
          atomicMax(
            &telemetry[${TELEMETRY_MAX_LINEAR_SPEED_BITS}u],
            bitcast<u32>(sqrt(max(speed2, 0.0))),
          );
          atomicMax(
            &telemetry[${TELEMETRY_MAX_ANGULAR_SPEED_BITS}u],
            bitcast<u32>(sqrt(max(angularSurfaceSpeed2, 0.0))),
          );
        }
      }
    `, [helpers]);
    this.reduceBodiesAfterKernel = reduceBodiesAfterShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', this.maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      poseHistory: storage(
        this.poseHistoryAttr,
        'vec4f',
        this.maxBodies * BODY_HISTORY_VEC4S,
      ).toReadOnly(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      sleepLinearSpeed: uniform(sleepLinear),
      sleepAngularSpeed: uniform(sleepAngular),
      sleepPositionCorrection: uniform(sleepPositionCorrection),
      sleepAngularCorrection: uniform(sleepAngularCorrection),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reduce Bodies After Solve');
    assertStorageBufferBudget('Sleeping Islands Reduce Bodies After Solve', 10);

    const updateCandidatesShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        shapes: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        poseHistory: ptr<storage, array<vec4f>, read>,
        candidateFrames: ptr<storage, array<u32>, read_write>,
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        sleepFrames: u32,
        sleepLinearSpeed: f32,
        sleepAngularSpeed: f32,
        sleepPositionCorrection: f32,
        sleepAngularCorrection: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let pos = positions[gid];
        if (pos.w <= 0.0) {
          candidateFrames[gid] = 0u;
          return;
        }

        let shape = shapes[gid];
        let q = quaternions[gid];
        let v = velocities[gid];
        let w = angularVelocities[gid];
        let prePos = poseHistory[gid * ${BODY_HISTORY_VEC4S}u + 2u];
        let preQ = poseHistory[gid * ${BODY_HISTORY_VEC4S}u + 3u];
        let boundingRadius = sleepBoundingRadius(shape);
        let speed2 = dot(v.xyz, v.xyz);
        let angularSurfaceSpeed2 = dot(w.xyz, w.xyz) * boundingRadius * boundingRadius;
        let correction = pos.xyz - prePos.xyz;
        let correction2 = dot(correction, correction);
        let angularSurfaceCorrection = sleepQuaternionDelta(q, preQ) * boundingRadius;
        let state = bodyState[gid];
        let valid = sleepFinite4(pos) && sleepShapeValid(shape) && sleepFinite4(q)
          && sleepFinite4(v) && sleepFinite4(w)
          && sleepFinite4(prePos) && sleepFinite4(preQ)
          && sleepFinite(boundingRadius) && boundingRadius >= 0.0
          && sleepFinite(speed2) && sleepFinite(angularSurfaceSpeed2)
          && sleepFinite(correction2) && sleepFinite(angularSurfaceCorrection);
        let locallyQuiet = valid
          && (state & ${BODY_CONSERVATIVE | SLEEPING_BODY_STATE_SLEEPING_BIT}u) == 0u
          && speed2 <= sleepLinearSpeed * sleepLinearSpeed
          && angularSurfaceSpeed2 <= sleepAngularSpeed * sleepAngularSpeed
          && correction2 <= sleepPositionCorrection * sleepPositionCorrection
          && angularSurfaceCorrection <= sleepAngularCorrection;
        let previous = candidateFrames[gid];
        let next = select(0u, min(previous + 1u, 0xfffffffeu), locallyQuiet);
        candidateFrames[gid] = next;
        atomicMax(&gateControl[${GATE_MAX_CANDIDATE}u], next);
        if (next >= sleepFrames) {
          atomicStore(&gateControl[${GATE_CANDIDATE_READY}u], 1u);
        }
      }
    `, [helpers]);
    this.updateCandidatesKernel = updateCandidatesShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', this.maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      poseHistory: storage(
        this.poseHistoryAttr,
        'vec4f',
        this.maxBodies * BODY_HISTORY_VEC4S,
      ).toReadOnly(),
      candidateFrames: storage(this.candidateFramesAttr, 'uint', this.maxBodies),
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      bodyCount: uniform(0),
      sleepFrames: uniform(this.sleepFrames),
      sleepLinearSpeed: uniform(sleepLinear),
      sleepAngularSpeed: uniform(sleepAngular),
      sleepPositionCorrection: uniform(sleepPositionCorrection),
      sleepAngularCorrection: uniform(sleepAngularCorrection),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Update Awake Fast Candidates');
    assertStorageBufferBudget('Sleeping Islands Update Awake Fast Candidates', 9);

    const resetCandidatesShader = wgslFn(/* wgsl */`
      fn compute(
        candidateFrames: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid < bodyCount) { candidateFrames[gid] = 0u; }
      }
    `);
    this.resetCandidatesBeforeKernel = resetCandidatesShader({
      candidateFrames: storage(this.candidateFramesAttr, 'uint', this.maxBodies),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reset Candidates Before Solve');
    this.resetCandidatesAfterKernel = resetCandidatesShader({
      candidateFrames: storage(this.candidateFramesAttr, 'uint', this.maxBodies),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reset Candidates After Solve');
    assertStorageBufferBudget('Sleeping Islands Reset Candidates Before Solve', 1);
    assertStorageBufferBudget('Sleeping Islands Reset Candidates After Solve', 1);

    const reduceContactsAfterShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        supportDirection: vec3f,
        supportNormalCosine: f32,
        sleepLinearSpeed: f32,
        wakeLinearSpeed: f32,
        wakeContactPenetration: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(pairActivity[${pairActiveContactsOffset}u], ${this.maxActivePairContacts}u);
        if (gid >= activeCount) { return; }
        let contact = pairActivity[${pairActiveContactsOffset + 1}u + gid];
        if (contact >= ${this.maxPairContacts}u) { return; }
        let base = contact * ${CONTACT_RECORD_VEC4S}u;
        let info = pairContacts[base + ${CONTACT_RECORD_META_OFFSET}u];
        let normalPen = pairContacts[base + ${CONTACT_RECORD_NORMAL_PEN_OFFSET}u];
        if (!sleepFinite4(info) || !sleepFinite4(normalPen) || info.z < 0.5) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          return;
        }
        let a = u32(info.x + 0.5);
        let b = u32(info.y + 0.5);
        if (a >= bodyCount || b >= bodyCount || a == b) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          return;
        }
        let dynamicA = positions[a].w > 0.0;
        let dynamicB = positions[b].w > 0.0;
        if (!dynamicA && !dynamicB) { return; }
        var root = select(a, b, dynamicB);
        if (dynamicA) { root = sleepFindRoot(parents, a, bodyCount); }
        if (root == 0xffffffffu) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
          return;
        }
        if (dynamicA && dynamicB) {
          let rootB = sleepFindRoot(parents, b, bodyCount);
          if (rootB != root) {
            atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_UNION_INCOMPLETE}u);
            atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_FAULT | ISLAND_INELIGIBLE}u);
            return;
          }
        } else {
          let n2 = dot(normalPen.xyz, normalPen.xyz);
          if (n2 > 1e-12 && sleepFinite(n2)) {
            let n = normalPen.xyz * inverseSqrt(n2);
            let supportDot = select(dot(-n, supportDirection), dot(n, supportDirection), dynamicB);
            if (dot(supportDirection, supportDirection) > 0.5 && supportDot >= supportNormalCosine) {
              atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_SUPPORTED}u);
            }
          }
        }
        let n2 = dot(normalPen.xyz, normalPen.xyz);
        if (!sleepFinite(n2) || n2 <= 1e-12) {
          atomicOr(&telemetry[${TELEMETRY_FLAGS}u], ${FRAME_FAIL_OPEN | FRAME_INVALID_INPUT}u);
          return;
        }
        let n = normalPen.xyz * inverseSqrt(n2);
        let relativeNormalSpeed = abs(dot(velocities[b].xyz - velocities[a].xyz, n));
        if (
          relativeNormalSpeed > wakeLinearSpeed
          || normalPen.w > wakeContactPenetration
        ) {
          atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_WAKE}u);
        }
        if (relativeNormalSpeed > sleepLinearSpeed) {
          atomicOr(&islandData[root * ${ISLAND_WORDS}u], ${ISLAND_CONTACT_INELIGIBLE}u);
          atomicAdd(&telemetry[${TELEMETRY_INELIGIBLE_CONTACTS}u], 1u);
        }
        if (normalPen.w >= 0.0) {
          atomicMax(
            &telemetry[${TELEMETRY_MAX_CONTACT_PENETRATION_BITS}u],
            bitcast<u32>(normalPen.w),
          );
        }
      }
    `, [helpers]);
    this.reduceContactsAfterKernel = reduceContactsAfterShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      supportDirection: uniform(supportDirection.clone()),
      supportNormalCosine: uniform(supportNormalCosine),
      sleepLinearSpeed: uniform(sleepLinear),
      wakeLinearSpeed: uniform(wakeLinear),
      wakeContactPenetration: uniform(wakeContactPenetration),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reduce Contacts After Solve');
    assertStorageBufferBudget('Sleeping Islands Reduce Contacts After Solve', 7);

    const updateDwellShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        stableFrames: ptr<storage, array<u32>, read_write>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let root = sleepFindRoot(parents, gid, bodyCount);
        if (root == 0xffffffffu) {
          stableFrames[gid] = 0u;
          return;
        }
        let flags = atomicLoad(&islandData[root * ${ISLAND_WORDS}u]);
        let globalFlags = atomicLoad(&telemetry[${TELEMETRY_FLAGS}u]);
        let mixed = (flags & ${ISLAND_HAS_SLEEPING | ISLAND_HAS_AWAKE}u)
          == ${ISLAND_HAS_SLEEPING | ISLAND_HAS_AWAKE}u;
        let hardEligible = (globalFlags & ${FRAME_FAIL_MASK}u) == 0u
          && (flags & ${ISLAND_SUPPORTED}u) != 0u
          && (flags & ${ISLAND_FAULT | ISLAND_WAKE}u) == 0u
          && !mixed;
        let bodyQuiet = (flags & ${ISLAND_INELIGIBLE}u) == 0u;
        let contactQuiet = (flags & ${ISLAND_CONTACT_INELIGIBLE}u) == 0u;
        if (hardEligible && bodyQuiet && contactQuiet) {
          stableFrames[gid] = min(stableFrames[gid] + 1u, 0xfffffffeu);
        } else if (!hardEligible) {
          stableFrames[gid] = 0u;
        } else if (!bodyQuiet) {
          // Moderate motion/correction inside the wake hysteresis band drains
          // dwell instead of erasing it. Sustained activity reaches zero;
          // one f32/contact-solver spike no longer loses an entire quiet run.
          stableFrames[gid] = max(stableFrames[gid], 1u) - 1u;
        }
        // A contact just across the sleep-penetration threshold is a soft
        // pause, not evidence that a nearly settled island became active.
        // Holding (rather than erasing) dwell makes it monotone across f32
        // manifold jitter. Moderate body residuals drain one frame at a time;
        // wake, topology faults, and loss of support remain hard resets.
      }
    `, [helpers]);
    this.updateDwellKernel = updateDwellShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      stableFrames: storage(this.stableFramesAttr, 'uint', this.maxBodies),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Update Dwell');
    assertStorageBufferBudget('Sleeping Islands Update Dwell', 5);

    const reduceDwellShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        stableFrames: ptr<storage, array<u32>, read>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveSleepState: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let root = sleepFindRoot(parents, gid, bodyCount);
        if (root != 0xffffffffu) {
          // Motionless poses alone are not a fixed point of AVBD. If the
          // current primal/dual/penalty residuals did not converge, freezing
          // this island would merely hide deferred augmented-Lagrangian work
          // and produce a large settle/jump when it wakes later.
          let configured = adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_POLISH_CONFIGURED}u]
            != 0u;
          let residualConverged = adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_CONVERGED}u]
            != 0u;
          let polishCompleted = adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_POLISH_COMPLETED}u]
            >= ${AVBD_ADAPTIVE_SLEEP_POLISH_STEPS}u;
          let flags = atomicLoad(&islandData[root * ${ISLAND_WORDS}u]);
          // Fixed-iteration scenes retain their previous sleep admission.
          // Adaptive awake islands fail closed until both the four-substep
          // polish proof and this substep's residual certificate are present.
          if (
            configured
            && (!polishCompleted || !residualConverged)
            && (flags & ${ISLAND_HAS_AWAKE}u) != 0u
          ) {
            atomicOr(
              &islandData[root * ${ISLAND_WORDS}u],
              ${ISLAND_ADAPTIVE_BLOCK}u,
            );
          }
          atomicMin(&islandData[root * ${ISLAND_WORDS}u + 2u], stableFrames[gid]);
        }
      }
    `, [helpers]);
    this.reduceDwellKernel = reduceDwellShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      stableFrames: storage(this.stableFramesAttr, 'uint', this.maxBodies).toReadOnly(),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      adaptiveSleepState: storage(
        adaptiveSleepState,
        'uint',
        AVBD_ADAPTIVE_STATE_WORDS,
      ).toReadOnly(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Reduce Dwell');
    assertStorageBufferBudget('Sleeping Islands Reduce Dwell', 4);

    const applySleepShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        bodyState: ptr<storage, array<u32>, read_write>,
        stableFrames: ptr<storage, array<u32>, read_write>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        poseHistory: ptr<storage, array<vec4f>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        sleepFrames: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        let historyBase = gid * ${BODY_HISTORY_VEC4S}u;
        let pos = positions[gid];
        let q = quaternions[gid];
        if (pos.w <= 0.0) {
          bodyState[gid] = ${BODY_INITIALIZED}u;
          stableFrames[gid] = 0u;
          poseHistory[historyBase] = pos;
          poseHistory[historyBase + 1u] = q;
          return;
        }
        let root = sleepFindRoot(parents, gid, bodyCount);
        var shouldSleep = false;
        var flags = ${ISLAND_FAULT}u;
        var memberCount = 0u;
        var minStable = 0u;
        if (root != 0xffffffffu) {
          let base = root * ${ISLAND_WORDS}u;
          flags = atomicLoad(&islandData[base]);
          memberCount = atomicLoad(&islandData[base + 1u]);
          minStable = atomicLoad(&islandData[base + 2u]);
          let globalFlags = atomicLoad(&telemetry[${TELEMETRY_FLAGS}u]);
          shouldSleep = (globalFlags & ${FRAME_FAIL_MASK}u) == 0u
            && (flags & ${ISLAND_SUPPORTED}u) != 0u
            && (flags & ${ISLAND_INELIGIBLE | ISLAND_CONTACT_INELIGIBLE | ISLAND_FAULT | ISLAND_WAKE | ISLAND_ADAPTIVE_BLOCK}u) == 0u
            && minStable >= sleepFrames;
        }
        let wasSleeping = (bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u;
        var state = ${BODY_INITIALIZED}u;
        if (shouldSleep) {
          state |= ${SLEEPING_BODY_STATE_SLEEPING_BIT}u;
          velocities[gid] = vec4f(0.0);
          angularVelocities[gid] = vec4f(0.0);
          atomicAdd(&telemetry[${TELEMETRY_SLEEPING_BODIES}u], 1u);
          if (!wasSleeping) {
            atomicAdd(&telemetry[${TELEMETRY_SLEEP_EVENTS}u], 1u);
          }
        } else if (wasSleeping) {
          stableFrames[gid] = 0u;
          atomicAdd(&telemetry[${TELEMETRY_WAKE_EVENTS}u], 1u);
        }
        bodyState[gid] = state;
        poseHistory[historyBase] = pos;
        poseHistory[historyBase + 1u] = q;

        if (gid == root) {
          atomicAdd(&telemetry[${TELEMETRY_ISLANDS}u], 1u);
          atomicMax(&telemetry[${TELEMETRY_MAX_ISLAND_SIZE}u], memberCount);
          atomicMax(&telemetry[${TELEMETRY_MAX_ISLAND_DWELL}u], minStable);
          if ((flags & ${ISLAND_SUPPORTED}u) != 0u) {
            atomicAdd(&telemetry[${TELEMETRY_SUPPORTED_ISLANDS}u], 1u);
          }
          if (
            (flags & ${ISLAND_SUPPORTED}u) != 0u
            && (flags & ${ISLAND_INELIGIBLE | ISLAND_CONTACT_INELIGIBLE | ISLAND_FAULT | ISLAND_WAKE | ISLAND_ADAPTIVE_BLOCK}u) == 0u
          ) {
            atomicAdd(&telemetry[${TELEMETRY_ELIGIBLE_ISLANDS}u], 1u);
          }
          if (shouldSleep) {
            atomicAdd(&telemetry[${TELEMETRY_SLEEPING_ISLANDS}u], 1u);
          }
        }
      }
    `, [helpers]);
    this.applySleepKernel = applySleepShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', this.maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', this.maxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', this.maxBodies),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies),
      stableFrames: storage(this.stableFramesAttr, 'uint', this.maxBodies),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      poseHistory: storage(
        this.poseHistoryAttr,
        'vec4f',
        this.maxBodies * BODY_HISTORY_VEC4S,
      ),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      sleepFrames: uniform(this.sleepFrames),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Apply Sleep');
    assertStorageBufferBudget('Sleeping Islands Apply Sleep', 10);

    const finalizePersistentGateShader = wgslFn(/* wgsl */`
      fn compute(
        gateControl: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        candidateResetAfterDispatch: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
      ) -> void {
        let bodyWorkgroups = (bodyCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        let path = atomicLoad(&gateControl[${GATE_PATH}u]);
        candidateResetAfterDispatch[0u] = 0u;
        candidateResetAfterDispatch[1u] = 1u;
        candidateResetAfterDispatch[2u] = 1u;

        if (
          path == ${GATE_PATH_FULL_VALIDATE}u
          || path == ${GATE_PATH_CACHED_ROOT}u
        ) {
          let sleepingBodies = atomicLoad(&telemetry[${TELEMETRY_SLEEPING_BODIES}u]);
          let maxDwell = atomicLoad(&telemetry[${TELEMETRY_MAX_ISLAND_DWELL}u]);
          atomicStore(&gateControl[${GATE_SLEEPING_BODIES}u], sleepingBodies);
          if (sleepingBodies == 0u && maxDwell == 0u) {
            // A loose body-only candidate can be unsupported or contact-noisy.
            // One exact DSU frame rejects it, then all candidates cool down
            // from zero so it cannot create a full-DSU storm every substep.
            atomicStore(&gateControl[${GATE_MODE}u], ${GATE_MODE_AWAKE_FAST}u);
            atomicStore(&gateControl[${GATE_CANDIDATE_READY}u], 0u);
            atomicStore(&gateControl[${GATE_MAX_CANDIDATE}u], 0u);
            candidateResetAfterDispatch[0u] = bodyWorkgroups;
            let fallbackCount = atomicAdd(
              &gateControl[${GATE_CANDIDATE_FALLBACKS}u],
              1u,
            ) + 1u;
            atomicStore(
              &telemetry[${TELEMETRY_CANDIDATE_FALLBACKS}u],
              fallbackCount,
            );
          } else {
            atomicStore(&gateControl[${GATE_MODE}u], ${GATE_MODE_FULL_VALIDATE}u);
          }
        } else {
          atomicStore(&gateControl[${GATE_SLEEPING_BODIES}u], 0u);
          atomicStore(&gateControl[${GATE_MODE}u], ${GATE_MODE_AWAKE_FAST}u);
        }

        let mode = atomicLoad(&gateControl[${GATE_MODE}u]);
        atomicStore(&telemetry[${TELEMETRY_GATE_MODE}u], mode);
        atomicStore(
          &telemetry[${TELEMETRY_MAX_CANDIDATE_FRAMES}u],
          atomicLoad(&gateControl[${GATE_MAX_CANDIDATE}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_AWAKE_FAST_SUBSTEPS}u],
          atomicLoad(&gateControl[${GATE_AWAKE_FAST_SUBSTEPS}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_FULL_DSU_SUBSTEPS}u],
          atomicLoad(&gateControl[${GATE_FULL_DSU_SUBSTEPS}u]),
        );
        atomicStore(
          &telemetry[${TELEMETRY_CANDIDATE_FALLBACKS}u],
          atomicLoad(&gateControl[${GATE_CANDIDATE_FALLBACKS}u]),
        );
      }
    `);
    this.finalizePersistentGateKernel = finalizePersistentGateShader({
      gateControl: storage(this.gateControlAttr, 'uint', GATE_CONTROL_WORDS).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      candidateResetAfterDispatch: storage(
        this.candidateResetAfterDispatchIndirectAttr,
        'uint',
        3,
      ),
      bodyCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Finalize Persistent GPU Gate');
    assertStorageBufferBudget('Sleeping Islands Finalize Persistent GPU Gate', 3);

    const syncSleepFlagsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        var word = bodyConstraintCounts[gid] & ~${BODY_SLEEP_FLAG}u;
        if (
          positions[gid].w > 0.0
          && (bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u
        ) {
          word |= ${BODY_SLEEP_FLAG}u;
        }
        bodyConstraintCounts[gid] = word;
      }
    `);
    this.syncSleepFlagsKernel = syncSleepFlagsShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', this.maxBodies),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Sync AVBD Flags');
    assertStorageBufferBudget('Sleeping Islands Sync AVBD Flags', 3);

    // The after-solve sync occupies the same dispatch slot as the lightweight
    // pre-solve sync, but also publishes the next adaptive polish request. This
    // keeps the control loop GPU-only without adding a node or queue submit.
    const syncSleepFlagsAfterShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        parents: ptr<storage, array<atomic<u32>>, read_write>,
        islandData: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveSleepState: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        sleepFrames: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        var word = bodyConstraintCounts[gid] & ~${BODY_SLEEP_FLAG}u;
        let dynamic = positions[gid].w > 0.0;
        let sleeping = (bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u;
        if (dynamic && sleeping) {
          word |= ${BODY_SLEEP_FLAG}u;
        }
        bodyConstraintCounts[gid] = word;

        if (
          atomicLoad(
            &adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_POLISH_CONFIGURED}u]
          ) == 0u
          || atomicLoad(&parents[gid]) != gid
        ) { return; }

        let base = gid * ${ISLAND_WORDS}u;
        let flags = atomicLoad(&islandData[base]);
        let mixed = (flags & ${ISLAND_HAS_SLEEPING | ISLAND_HAS_AWAKE}u)
          == ${ISLAND_HAS_SLEEPING | ISLAND_HAS_AWAKE}u;
        let globalFlags = atomicLoad(&telemetry[${TELEMETRY_FLAGS}u]);
        let physicallyEligible = (globalFlags & ${FRAME_FAIL_MASK}u) == 0u
          && (flags & ${ISLAND_SUPPORTED}u) != 0u
          && (flags & ${ISLAND_HAS_AWAKE}u) != 0u
          && (flags & ${ISLAND_INELIGIBLE | ISLAND_CONTACT_INELIGIBLE | ISLAND_FAULT | ISLAND_WAKE}u) == 0u
          && !mixed;
        let requestThreshold = sleepFrames - min(
          sleepFrames,
          ${AVBD_ADAPTIVE_SLEEP_POLISH_STEPS}u,
        );
        let minStable = atomicLoad(&islandData[base + 2u]);
        // The DSU root may be a static support body. Avoid inspecting the
        // root's own dynamic/sleep bit; HAS_AWAKE is the island-wide source of
        // truth. If this exact pass admitted an island at its sleep threshold,
        // applySleep already transitioned every member and no next request is
        // needed even though the pre-apply aggregate still says HAS_AWAKE.
        let admittedAtSleepThreshold = atomicLoad(
          &adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_POLISH_COMPLETED}u]
        ) >= ${AVBD_ADAPTIVE_SLEEP_POLISH_STEPS}u
          && atomicLoad(
            &adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_CONVERGED}u]
          ) != 0u
          && minStable >= sleepFrames;
        if (
          physicallyEligible
          && minStable >= requestThreshold
          && !admittedAtSleepThreshold
        ) {
          atomicStore(
            &adaptiveSleepState[${AVBD_ADAPTIVE_SLEEP_POLISH_REQUESTED}u],
            1u,
          );
        }
      }
    `);
    this.syncSleepFlagsAfterKernel = syncSleepFlagsAfterShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', this.maxBodies),
      parents: storage(this.parentAttr, 'uint', this.maxBodies).toAtomic(),
      islandData: storage(
        this.islandDataAttr,
        'uint',
        this.maxBodies * ISLAND_WORDS,
      ).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      adaptiveSleepState: storage(
        adaptiveSleepState,
        'uint',
        AVBD_ADAPTIVE_STATE_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      sleepFrames: uniform(this.sleepFrames),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Sync AVBD Flags And Request Polish');
    assertStorageBufferBudget('Sleeping Islands Sync AVBD Flags And Request Polish', 7);

    const selectSolverContactsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        solverContactDispatchIndirect: ptr<storage, array<u32>, read_write>,
      ) -> void {
        let canonicalCount = min(
          atomicLoad(&pairActivity[${this.pairActiveContactsOffset}u]),
          ${this.maxActivePairContacts}u,
        );
        let allAwake = atomicLoad(
          &telemetry[${TELEMETRY_PRE_SOLVE_SLEEPING_BODIES}u],
        ) == 0u;
        atomicStore(&telemetry[${TELEMETRY_SOLVER_LIST_OVERFLOWS}u], 0u);
        if (allAwake) {
          atomicStore(
            &pairActivity[${this.pairSolverContactsOffset}u],
            ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u | canonicalCount,
          );
          atomicStore(
            &telemetry[${TELEMETRY_SOLVER_CONTACTS}u],
            canonicalCount,
          );
          atomicStore(
            &telemetry[${TELEMETRY_SOLVER_CANONICAL_ALIAS}u],
            1u,
          );
          // Suppress the expensive atomic reservation/copy pass. The direct
          // finalizer still runs and validates/publishes this alias header.
          solverContactDispatchIndirect[0] = 0u;
        } else {
          atomicStore(&pairActivity[${this.pairSolverContactsOffset}u], 0u);
          atomicStore(&telemetry[${TELEMETRY_SOLVER_CONTACTS}u], 0u);
          atomicStore(
            &telemetry[${TELEMETRY_SOLVER_CANONICAL_ALIAS}u],
            0u,
          );
        }
      }
    `);
    this.selectSolverContactsKernel = selectSolverContactsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      solverContactDispatchIndirect: storage(
        this.solverContactDispatchIndirectAttr,
        'uint',
        3,
      ),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Select Solver Contact List');
    assertStorageBufferBudget('Sleeping Islands Select Solver Contact List', 3);

    const compactAwakeSolverContactsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          atomicLoad(&pairActivity[${this.pairActiveContactsOffset}u]),
          ${this.maxActivePairContacts}u,
        );
        if (gid >= activeCount) { return; }
        let contact = atomicLoad(
          &pairActivity[${this.pairActiveContactsOffset + 1}u + gid],
        );

        // Invalid records are retained so AVBD's normal validation/skip path
        // sees the same stream as the canonical list. Sleeping DSU telemetry
        // already marks the frame fatal; compaction must not hide corruption.
        var keep = true;
        if (contact < ${this.maxPairContacts}u) {
          let info = pairContacts[
            contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
          ];
          let a = u32(info.x + 0.5);
          let b = u32(info.y + 0.5);
          if (a < bodyCount && b < bodyCount && a != b && info.z >= 0.5) {
            atomicAdd(&telemetry[${TELEMETRY_COMPACT_VALID_CONTACTS}u], 1u);
            // Static bodies never receive an AVBD primal update. Treat them
            // as inactive endpoints here so a fully sleeping supported island
            // has exactly zero solver contacts, including floor supports.
            let awakeA = positions[a].w > 0.0
              && (bodyState[a] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) == 0u;
            let awakeB = positions[b].w > 0.0
              && (bodyState[b] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) == 0u;
            keep = awakeA || awakeB;
            if (!keep) {
              atomicAdd(
                &telemetry[${TELEMETRY_COMPACT_SLEEPING_FILTERED}u],
                1u,
              );
            }
          } else {
            atomicAdd(
              &telemetry[${TELEMETRY_COMPACT_INVALID_RETAINED}u],
              1u,
            );
          }
        } else {
          atomicAdd(&telemetry[${TELEMETRY_COMPACT_INVALID_RETAINED}u], 1u);
        }
        if (!keep) { return; }

        let outIndex = atomicAdd(
          &pairActivity[${this.pairSolverContactsOffset}u],
          1u,
        );
        if (outIndex >= ${this.pairSolverContactsCapacity}u) {
          // Never clamp or publish a partial count as if it were complete.
          // The capacity precondition makes this unreachable for a valid
          // canonical list, but a violated invariant is a fatal GPU signal.
          atomicOr(
            &telemetry[${TELEMETRY_FLAGS}u],
            ${FRAME_FAIL_OPEN | FRAME_SOLVER_LIST_OVERFLOW}u,
          );
          atomicAdd(&telemetry[${TELEMETRY_SOLVER_LIST_OVERFLOWS}u], 1u);
          return;
        }
        atomicStore(
          &pairActivity[${this.pairSolverContactsOffset + 1}u + outIndex],
          contact,
        );
        atomicAdd(&telemetry[${TELEMETRY_SOLVER_CONTACTS}u], 1u);
      }
    `);
    this.compactAwakeSolverContactsKernel = compactAwakeSolverContactsShader({
      positions: storage(positions, 'vec4f', this.maxBodies).toReadOnly(),
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Compact Awake Solver Contacts');
    assertStorageBufferBudget('Sleeping Islands Compact Awake Solver Contacts', 5);

    const finalizeSolverContactsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        telemetry: ptr<storage, array<atomic<u32>>, read_write>,
      ) -> void {
        let rawSolverCount = atomicLoad(
          &pairActivity[${this.pairSolverContactsOffset}u],
        );
        if (
          (rawSolverCount & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u
        ) {
          let canonicalCount = min(
            atomicLoad(&pairActivity[${this.pairActiveContactsOffset}u]),
            ${this.maxActivePairContacts}u,
          );
          // Rebuild the header from the canonical count instead of trusting
          // stale low bits. This also keeps CPU telemetry/readback coherent.
          atomicStore(
            &pairActivity[${this.pairSolverContactsOffset}u],
            ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u | canonicalCount,
          );
          atomicStore(
            &telemetry[${TELEMETRY_SOLVER_CONTACTS}u],
            canonicalCount,
          );
          atomicStore(
            &telemetry[${TELEMETRY_SOLVER_CANONICAL_ALIAS}u],
            1u,
          );
          return;
        }
        if (rawSolverCount <= ${this.pairSolverContactsCapacity}u) {
          atomicStore(&telemetry[${TELEMETRY_SOLVER_CONTACTS}u], rawSolverCount);
          atomicStore(
            &telemetry[${TELEMETRY_SOLVER_CANONICAL_ALIAS}u],
            0u,
          );
          return;
        }

        // A valid compact reservation cannot overflow because scratchCapacity
        // is at least maxActivePairContacts. If memory corruption violates that
        // proof, fail loudly and alias the complete canonical list rather than
        // solving a truncated subset or copying O(canonicalCount) records.
        let canonicalCount = min(
          atomicLoad(&pairActivity[${this.pairActiveContactsOffset}u]),
          ${this.maxActivePairContacts}u,
        );
        atomicOr(
          &telemetry[${TELEMETRY_FLAGS}u],
          ${FRAME_FAIL_OPEN | FRAME_SOLVER_LIST_OVERFLOW}u,
        );
        atomicMax(&telemetry[${TELEMETRY_SOLVER_LIST_OVERFLOWS}u], 1u);
        atomicStore(
          &pairActivity[${this.pairSolverContactsOffset}u],
          ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u | canonicalCount,
        );
        atomicStore(&telemetry[${TELEMETRY_SOLVER_CONTACTS}u], canonicalCount);
        atomicStore(
          &telemetry[${TELEMETRY_SOLVER_CANONICAL_ALIAS}u],
          1u,
        );
      }
    `);
    this.finalizeSolverContactsKernel = finalizeSolverContactsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivity.count).toAtomic(),
      telemetry: storage(this.telemetryAttr, 'uint', TELEMETRY_WORDS).toAtomic(),
    }).computeKernel([1, 1, 1]).setName('Sleeping Islands Finalize Solver Contact List');
    assertStorageBufferBudget('Sleeping Islands Finalize Solver Contact List', 2);

    const maskActiveSleepingContactsShader = wgslFn(/* wgsl */`
      fn compute(
        bodyState: ptr<storage, array<u32>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          pairActivity[${pairActiveContactsOffset}u],
          ${this.maxActivePairContacts}u,
        );
        if (gid >= activeCount) { return; }
        let address = ${pairActiveContactsOffset + 1}u + gid;
        let contact = pairActivity[address];
        if (contact >= ${this.maxPairContacts}u) { return; }
        let info = pairContacts[
          contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
        ];
        let a = u32(info.x + 0.5);
        let b = u32(info.y + 0.5);
        if (a >= bodyCount || b >= bodyCount) { return; }
        let sleepingA = (bodyState[a] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u;
        let sleepingB = (bodyState[b] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u;
        if (sleepingA && sleepingB) {
          // Keep the GPU-owned header unchanged. The solver/capture lanes
          // already treat maxPairContacts as an invalid compact-list entry,
          // while awake-sleep and static-sleep rows remain available to wake.
          pairActivity[address] = ${this.maxPairContacts}u;
        }
      }
    `);
    this.maskActiveSleepingContactsKernel = maskActiveSleepingContactsShader({
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivity.count),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Mask Active Sleep-Sleep Contacts');
    assertStorageBufferBudget('Sleeping Islands Mask Active Sleep-Sleep Contacts', 3);

    const filterSleepingContactsShader = wgslFn(/* wgsl */`
      fn compute(
        bodyState: ptr<storage, array<u32>, read>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        pairBodyContactIndices: ptr<storage, array<u32>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let body = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (
          body >= bodyCount
          || (bodyState[body] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) == 0u
        ) { return; }
        let requested = min(pairBodyContactCounts[body], 0xffffu);
        let directCount = min(requested, ${maxPairContactsPerBody}u);
        let directBase = body * ${maxPairContactsPerBody}u;
        for (var k = 0u; k < directCount; k++) {
          let address = directBase + k;
          let contact = pairBodyContactIndices[address];
          if (contact >= ${this.maxPairContacts}u) { continue; }
          let info = pairContacts[
            contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
          ];
          let a = u32(info.x + 0.5);
          let b = u32(info.y + 0.5);
          let other = select(a, b, a == body);
          if (
            other < bodyCount
            && (bodyState[other] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u
          ) {
            pairBodyContactIndices[address] = ${this.maxPairContacts}u;
          }
        }
        var node = pairBodyContactIndices[${pairBodyOverflowHeadOffset}u + body];
        var visited = 0u;
        loop {
          if (
            node == 0xffffffffu
            || node >= ${pairBodyOverflowCapacity}u
            || visited >= ${pairBodyOverflowCapacity}u
          ) { break; }
          let nodeBase = ${pairBodyOverflowNodeOffset}u + node * 2u;
          let contact = pairBodyContactIndices[nodeBase];
          if (contact < ${this.maxPairContacts}u) {
            let info = pairContacts[
              contact * ${CONTACT_RECORD_VEC4S}u + ${CONTACT_RECORD_META_OFFSET}u
            ];
            let a = u32(info.x + 0.5);
            let b = u32(info.y + 0.5);
            let other = select(a, b, a == body);
            if (
              other < bodyCount
              && (bodyState[other] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u
            ) {
              pairBodyContactIndices[nodeBase] = ${this.maxPairContacts}u;
            }
          }
          node = pairBodyContactIndices[nodeBase + 1u];
          visited += 1u;
        }
      }
    `);
    this.filterSleepingContactsKernel = filterSleepingContactsShader({
      bodyState: storage(this.bodyStateAttr, 'uint', this.maxBodies).toReadOnly(),
      pairContacts: storage(
        pairContacts,
        'vec4f',
        this.maxPairContacts * CONTACT_RECORD_VEC4S,
      ).toReadOnly(),
      pairBodyContactCounts: storage(
        pairBodyContactCounts,
        'uint',
        this.maxBodies,
      ).toReadOnly(),
      pairBodyContactIndices: storage(
        pairBodyContactIndices,
        'uint',
        pairBodyContactIndices.count,
      ),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Sleeping Islands Filter Sleep-Sleep Contacts');
    assertStorageBufferBudget('Sleeping Islands Filter Sleep-Sleep Contacts', 4);
    } catch (error) {
      // PhysicsEngine cannot own/dispose this stage until the constructor
      // returns. Close the post-allocation shader/attribute construction
      // window explicitly so a device-limit or TSL build error leaks nothing.
      this.cachePairSeenBitsAllocation.destroy();
      this.cacheSeenBitsAllocation.destroy();
      this.cachedPairKeyAllocation.destroy();
      this.cachedBodyRootAllocation.destroy();
      throw error;
    }
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.invalidateZeroWorkCertificate();
    this.enabled = enabled;
    if (!enabled && this.mayHaveSleepingBodies) this.wakeFlushPending = true;
  }

  private invalidateZeroWorkCertificate(): void {
    this.mutationEpoch = (this.mutationEpoch + 1) >>> 0;
    this.zeroWorkEligible = false;
    this.zeroWorkConfirmedFrameId = -1;
    this.zeroWorkConfirmedBodyCount = 0;
    this.zeroWorkConfirmedTopologyGeneration = null;
    this.zeroWorkConfirmedGravity = null;
    this.zeroWorkInvalidations++;
    // The GPU body-state mask is valid only for the exact submitted mutation
    // generation that produced it. Every public mutation fails open before a
    // later broadphase can omit even one sleeping query body.
    this.sparseBroadphaseEligible = false;
    this.sparseBroadphaseConfirmedFrameId = -1;
    this.sparseBroadphaseConfirmedBodyCount = 0;
    this.sparseBroadphaseConfirmedTopologyGeneration = null;
    this.sparseBroadphaseConfirmedMutationEpoch = -1;
    this.sparseBroadphaseGeneration = (this.sparseBroadphaseGeneration + 1) >>> 0;
    // A map submitted before this mutation must not re-arm the certificate.
    this.readbackGeneration++;
  }

  requestReset(): void {
    this.invalidateZeroWorkCertificate();
    this.resetRequested = true;
    this.appendStartBodyCount = null;
    this.wakeFlushPending = this.mayHaveSleepingBodies;
    this.preparedFrameId = -1;
  }

  requestWakeAll(): void {
    this.invalidateZeroWorkCertificate();
    this.wakeFlushPending = true;
    this.appendStartBodyCount = null;
  }

  /** Local pose/velocity/control edits retain island locality but forbid a CPU zero-work step. */
  requestLocalMutation(): void {
    this.invalidateZeroWorkCertificate();
  }

  /**
   * Announces a pure tail append. Existing body state and sleeping flags stay
   * intact; the new suffix is initialized awake, the published topology cache
   * misses on generation, and full DSU wakes only the connected island.
   */
  requestBodyAppend(previousBodyCount: number): void {
    this.invalidateZeroWorkCertificate();
    if (this.resetRequested || this.wakeFlushPending) return;
    const start = Math.max(0, Math.min(this.maxBodies, Math.floor(previousBodyCount)));
    this.appendStartBodyCount = this.appendStartBodyCount === null
      ? start
      : Math.min(this.appendStartBodyCount, start);
    this.preparedFrameId = -1;
  }

  canSkipAllPhysics(params: SleepingIslandsFrameParams): boolean {
    const topologyGeneration = params.topologyGeneration === undefined
      ? null
      : (params.topologyGeneration >>> 0);
    const gravity = params.gravity;
    const gravityMatches = gravity !== undefined
      && this.zeroWorkConfirmedGravity !== null
      && gravity.every((value, axis) => (
        Number.isFinite(value)
        && Object.is(Math.fround(value), this.zeroWorkConfirmedGravity![axis])
      ));
    return !this.disposed
      && this.enabled
      && this.zeroWorkEligible
      && !this.resetRequested
      && !this.wakeFlushPending
      && this.appendStartBodyCount === null
      && !params.forceWake
      && (params.jointCount ?? 0) === 0
      && (params.springCount ?? 0) === 0
      && !params.restitutionActive
      && Math.floor(params.bodyCount) === this.zeroWorkConfirmedBodyCount
      && topologyGeneration !== null
      && topologyGeneration === this.zeroWorkConfirmedTopologyGeneration
      && this.mutationEpoch === this.completedMutationEpoch
      && gravityMatches;
  }

  recordZeroWorkFixedStep(): void {
    if (this.zeroWorkEligible) this.zeroWorkSkippedFixedSteps++;
  }

  getZeroWorkDiagnostics(): SleepingZeroWorkDiagnostics {
    return Object.freeze({
      eligible: this.zeroWorkEligible,
      confirmedFrameId: this.zeroWorkConfirmedFrameId,
      confirmedBodyCount: this.zeroWorkConfirmedBodyCount,
      confirmedTopologyGeneration: this.zeroWorkConfirmedTopologyGeneration,
      mutationEpoch: this.mutationEpoch,
      completedMutationEpoch: this.completedMutationEpoch,
      completedFrameId: this.completedFrameId,
      completedFrameSubmitted: this.completedFrameSubmitted,
      confirmations: this.zeroWorkConfirmations,
      skippedFixedSteps: this.zeroWorkSkippedFixedSteps,
      invalidations: this.zeroWorkInvalidations,
    });
  }

  buildBeforeSolveComputeNodes(params: SleepingIslandsFrameParams): any[] {
    this.beforeSolveComputeGroups = [];
    this.beforeSolveComputeNodeCount = 0;
    if (this.disposed) return [];
    const bodyCount = Math.max(0, Math.min(this.maxBodies, Math.floor(params.bodyCount)));
    let preserveAppend = this.appendStartBodyCount !== null
      && bodyCount > this.appendStartBodyCount
      && !this.resetRequested
      && !this.wakeFlushPending;
    if (params.topologyGeneration !== undefined) {
      const generation = params.topologyGeneration >>> 0;
      if (this.topologyGeneration !== undefined && generation !== this.topologyGeneration) {
        if (!preserveAppend) this.requestReset();
      }
      this.topologyGeneration = generation;
    }
    preserveAppend = preserveAppend && !this.resetRequested && !this.wakeFlushPending;
    const compatible = this.enabled
      && (params.jointCount ?? 0) === 0
      && (params.springCount ?? 0) === 0
      && !params.restitutionActive;
    const forceWake = !!params.forceWake || this.wakeFlushPending;
    const resetState = this.resetRequested;
    const nodes: any[] = [];
    const finish = (): any[] => {
      this.beforeSolveComputeGroups = nodes.length > 0 ? [nodes] : [];
      this.beforeSolveComputeNodeCount = nodes.length;
      return nodes;
    };
    const enqueue = (kernel: any, count: any): void => {
      kernel.count = count;
      nodes.push(kernel);
    };
    if (bodyCount <= 0) {
      this.preparedFrameId = -1;
      this.mayHaveSleepingBodies = false;
      this.appendStartBodyCount = null;
      return finish();
    }
    if (
      !this.solverContactCompactionEnabled
      && !compatible
      && !forceWake
      && !this.mayHaveSleepingBodies
    ) {
      this.preparedFrameId = -1;
      this.appendStartBodyCount = null;
      return finish();
    }

    let initialFlags = 0;
    if (!compatible) initialFlags |= FRAME_INCOMPATIBLE;
    if (forceWake) initialFlags |= FRAME_FORCED_WAKE;
    if (resetState) initialFlags |= FRAME_RESET;
    this.clearTelemetryKernel.computeNode.parameters.frameId.value = params.frameId >>> 0;
    this.clearTelemetryKernel.computeNode.parameters.initialFlags.value = initialFlags;
    enqueue(this.clearTelemetryKernel, [1, 1, 1]);

    const bodyWorkgroups = Math.ceil(bodyCount / WORKGROUP_SIZE);
    const beginParams = this.beginFrameKernel.computeNode.parameters;
    beginParams.bodyCount.value = bodyCount;
    beginParams.compatible.value = compatible ? 1 : 0;
    beginParams.resetState.value = resetState ? 1 : 0;
    beginParams.forceWake.value = forceWake ? 1 : 0;
    beginParams.initializeFromBody.value = preserveAppend
      ? this.appendStartBodyCount!
      : bodyCount;
    enqueue(this.beginFrameKernel, [bodyWorkgroups, 1, 1]);

    this.planPersistentGateKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.planPersistentGateKernel.computeNode.parameters.dynamicBodyCount.value = Math.max(
      0,
      Math.min(bodyCount, Math.floor(params.dynamicBodyCount)),
    );
    this.planPersistentGateKernel.computeNode.parameters.topologyKnown.value =
      params.topologyGeneration === undefined ? 0 : 1;
    this.planPersistentGateKernel.computeNode.parameters.topologyGeneration.value =
      params.topologyGeneration === undefined ? 0 : (params.topologyGeneration >>> 0);
    enqueue(this.planPersistentGateKernel, [1, 1, 1]);
    // The planner writes the following indirect arguments. WebGPU validates
    // usage per dispatch, and the reset shader does not bind that argument
    // buffer as storage, so ordered producer/consumer dispatches share a pass.
    this.resetCandidatesBeforeKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(
      this.resetCandidatesBeforeKernel,
      this.candidateResetBeforeDispatchIndirectAttr,
    );

    this.buildContactDispatchKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.buildContactDispatchKernel, [1, 1, 1]);
    this.planTopologyDispatchKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.planTopologyDispatchKernel, [1, 1, 1]);
    enqueue(this.clearCacheSeenBitsKernel, this.cacheSeenClearDispatchIndirectAttr);
    this.validateCachedBodiesKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(
      this.validateCachedBodiesKernel,
      this.cacheValidationBodyDispatchIndirectAttr,
    );
    this.validateCachedContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(
      this.validateCachedContactsKernel,
      this.cacheValidationContactDispatchIndirectAttr,
    );
    this.finalizeCachedTopologyKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.finalizeCachedTopologyKernel, [1, 1, 1]);
    enqueue(this.planCacheStagingSeenClearKernel, [1, 1, 1]);
    this.syncSleepFlagsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    if (!compatible) {
      enqueue(this.syncSleepFlagsKernel, [bodyWorkgroups, 1, 1]);
      if (this.solverContactCompactionEnabled) {
        enqueue(this.selectSolverContactsKernel, [1, 1, 1]);
        this.compactAwakeSolverContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
        enqueue(
          this.compactAwakeSolverContactsKernel,
          this.solverContactDispatchIndirectAttr,
        );
        enqueue(this.finalizeSolverContactsKernel, [1, 1, 1]);
      }
      this.preparedFrameId = -1;
      this.preparedBodyCount = 0;
      this.mayHaveSleepingBodies = false;
      this.wakeFlushPending = false;
      this.resetRequested = false;
      this.appendStartBodyCount = null;
      return finish();
    }

    this.fastGateCleanupKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.fastGateCleanupKernel, this.fastCleanupDispatchIndirectAttr);
    if (bodyCount <= SERIAL_DSU_BODY_THRESHOLD) {
      this.serialDsuKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(this.serialDsuKernel, this.serialDsuDispatchIndirectAttr);
    } else {
      this.initializeDsuKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(this.initializeDsuKernel, this.fullDsuBodyDispatchIndirectAttr);
      this.unionContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      this.pointerJumpKernel.computeNode.parameters.bodyCount.value = bodyCount;
      this.compressParentsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      for (let pass = 0; pass < this.unionPasses; pass++) {
        enqueue(this.unionContactsKernel, this.unionContactDispatchIndirectAttr);
        enqueue(this.pointerJumpKernel, this.unionBodyDispatchIndirectAttr);
        // Every finalizer consumes a distinct indirect argument and writes the
        // next one. The current dispatch therefore never binds one buffer as
        // both INDIRECT and writable STORAGE. atomicExchange supplies the next
        // round's clear, while convergence zeros contact/body and continuation
        // arguments so the complete unused suffix becomes zero work.
        enqueue(
          this.finishUnionRoundKernels[pass]!,
          this.unionRoundDispatchIndirectAttrs[pass]!,
        );
      }
      // The first-round argument remains one throughout a real parallel DSU
      // frame and is zero on serial/cached/awake-fast paths. Reuse it to run a
      // bounded epilogue that makes every continuation argument one-shot and
      // prevents stale finalizers on the next cached-root frame.
      for (const clearContinuationKernel of this.clearUnionRoundContinuationKernels) {
        enqueue(clearContinuationKernel, this.unionRoundDispatchIndirectAttr);
      }
      // One direct final compression also validates the bounded root-depth
      // invariant before any island aggregate can affect sleeping state.
      enqueue(this.compressParentsKernel, this.fullDsuBodyDispatchIndirectAttr);
    }
    // Full DSU cache publication is deliberately two-stage. The indirect
    // lanes are zero on a cache hit, while a same-command-buffer validation
    // miss enables them before these nodes execute.
    enqueue(this.clearCacheStagingKernel, this.cacheStagingSeenClearDispatchIndirectAttr);
    this.stageCachedBodiesKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.stageCachedBodiesKernel, this.fullDsuBodyDispatchIndirectAttr);
    this.stageCachedContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.stageCachedContactsKernel, this.fullDsuContactDispatchIndirectAttr);
    const publishParams = this.publishCachedTopologyKernel.computeNode.parameters;
    publishParams.bodyCount.value = bodyCount;
    publishParams.topologyKnown.value = params.topologyGeneration === undefined ? 0 : 1;
    publishParams.topologyGeneration.value = params.topologyGeneration === undefined
      ? 0
      : (params.topologyGeneration >>> 0);
    enqueue(this.publishCachedTopologyKernel, [1, 1, 1]);
    this.clearIslandsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.clearIslandsKernel.computeNode.parameters.preserveContactState.value = 0;
    enqueue(this.clearIslandsKernel, this.stageBodyDispatchIndirectAttr);
    this.reduceBodiesBeforeKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.reduceBodiesBeforeKernel, this.stageBodyDispatchIndirectAttr);
    this.reduceContactsBeforeKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.reduceContactsBeforeKernel, this.contactDispatchIndirectAttr);
    this.applyWakeKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.applyWakeKernel, this.stageBodyDispatchIndirectAttr);
    if (this.solverContactCompactionEnabled) {
      // These remain separate ordered dispatches inside one compute pass:
      // applyWake publishes state, selection publishes the indirect length,
      // compaction consumes it, and the finalizer validates the result.
      enqueue(this.selectSolverContactsKernel, [1, 1, 1]);
      this.compactAwakeSolverContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(
        this.compactAwakeSolverContactsKernel,
        this.solverContactDispatchIndirectAttr,
      );
      enqueue(this.finalizeSolverContactsKernel, [1, 1, 1]);
    } else {
      // Scratch capacity is insufficient: retain the established in-place
      // invalid-sentinel mask. This fallback is selected at construction and
      // AVBD receives the canonical active-list offset.
      this.maskActiveSleepingContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(this.maskActiveSleepingContactsKernel, this.contactDispatchIndirectAttr);
    }
    this.freezeSleepingKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.freezeSleepingKernel, this.stageBodyDispatchIndirectAttr);
    this.filterSleepingContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.filterSleepingContactsKernel, this.stageBodyDispatchIndirectAttr);

    this.preparedFrameId = params.frameId >>> 0;
    this.preparedBodyCount = bodyCount;
    this.resetRequested = false;
    this.wakeFlushPending = false;
    this.appendStartBodyCount = null;
    return finish();
  }

  beforeSolve(renderer: any, params: SleepingIslandsFrameParams): boolean {
    const nodes = this.buildBeforeSolveComputeNodes(params);
    if (nodes.length === 0) return false;
    for (const group of this.beforeSolveComputeGroups) {
      renderer.compute(group);
    }
    return true;
  }

  buildAfterSolveComputeNodes(params: SleepingIslandsFrameParams): any[] {
    this.afterSolveComputeGroups = [];
    this.afterSolveComputeNodeCount = 0;
    if (this.disposed) return [];
    const frameId = params.frameId >>> 0;
    const bodyCount = Math.max(0, Math.min(this.maxBodies, Math.floor(params.bodyCount)));
    if (frameId !== this.preparedFrameId || bodyCount !== this.preparedBodyCount || bodyCount <= 0) {
      return [];
    }
    const nodes: any[] = [];
    const finish = (): any[] => {
      this.afterSolveComputeGroups = nodes.length > 0 ? [nodes] : [];
      this.afterSolveComputeNodeCount = nodes.length;
      return nodes;
    };
    const enqueue = (kernel: any, count: any): void => {
      kernel.count = count;
      nodes.push(kernel);
    };
    this.clearIslandsAfterKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.clearIslandsAfterKernel, this.stageBodyDispatchIndirectAttr);
    this.reduceBodiesAfterKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.reduceBodiesAfterKernel, this.stageBodyDispatchIndirectAttr);
    this.reduceContactsAfterKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.reduceContactsAfterKernel, this.contactDispatchIndirectAttr);
    this.updateDwellKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.updateDwellKernel, this.stageBodyDispatchIndirectAttr);
    this.reduceDwellKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.reduceDwellKernel, this.stageBodyDispatchIndirectAttr);
    this.applySleepKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.applySleepKernel, this.stageBodyDispatchIndirectAttr);
    this.syncSleepFlagsAfterKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.syncSleepFlagsAfterKernel, this.stageBodyDispatchIndirectAttr);
    if (!this.solverContactCompactionEnabled) {
      this.maskActiveSleepingContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(this.maskActiveSleepingContactsKernel, this.contactDispatchIndirectAttr);
    }
    this.filterSleepingContactsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.filterSleepingContactsKernel, this.stageBodyDispatchIndirectAttr);

    this.updateCandidatesKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.updateCandidatesKernel, this.candidateUpdateDispatchIndirectAttr);
    this.finalizePersistentGateKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(this.finalizePersistentGateKernel, [1, 1, 1]);
    // The finalizer writes candidateResetAfterDispatch; the reset shader uses
    // it only as indirect input in the following dispatch, so one pass is valid.
    this.resetCandidatesAfterKernel.computeNode.parameters.bodyCount.value = bodyCount;
    enqueue(
      this.resetCandidatesAfterKernel,
      this.candidateResetAfterDispatchIndirectAttr,
    );

    this.preparedFrameId = -1;
    this.preparedBodyCount = 0;
    this.mayHaveSleepingBodies = true;
    // A later queue-ordered telemetry readback may certify this exact CPU
    // mutation/topology generation. Merely encoding the work is not enough.
    this.completedMutationEpoch = this.mutationEpoch;
    this.completedBodyCount = bodyCount;
    this.completedTopologyGeneration = this.topologyGeneration === undefined
      ? null
      : (this.topologyGeneration >>> 0);
    this.completedFrameId = frameId;
    this.completedFrameSubmitted = false;
    this.completedGravity = params.gravity !== undefined
      && params.gravity.every(Number.isFinite)
      ? [
          Math.fround(params.gravity[0]),
          Math.fround(params.gravity[1]),
          Math.fround(params.gravity[2]),
        ]
      : null;
    return finish();
  }

  afterSolve(renderer: any, params: SleepingIslandsFrameParams): boolean {
    const nodes = this.buildAfterSolveComputeNodes(params);
    if (nodes.length === 0) return false;
    for (const group of this.afterSolveComputeGroups) {
      renderer.compute(group);
    }
    return true;
  }

  private publishSparseBroadphaseCertificateIfProven(): void {
    const canPublishSparseCertificate = this.sparseBodyStateGpuPublicationProven
      && this.enabled
      && this.completedFrameSubmitted
      && this.completedMutationEpoch === this.mutationEpoch
      && this.completedBodyCount > 0
      && this.completedTopologyGeneration !== null
      && !this.resetRequested
      && !this.wakeFlushPending
      && this.appendStartBodyCount === null;
    if (!canPublishSparseCertificate) return;
    const changed = !this.sparseBroadphaseEligible
      || this.sparseBroadphaseConfirmedFrameId !== this.completedFrameId
      || this.sparseBroadphaseConfirmedBodyCount !== this.completedBodyCount
      || this.sparseBroadphaseConfirmedTopologyGeneration
        !== this.completedTopologyGeneration
      || this.sparseBroadphaseConfirmedMutationEpoch !== this.completedMutationEpoch;
    this.sparseBroadphaseEligible = true;
    this.sparseBroadphaseConfirmedFrameId = this.completedFrameId;
    this.sparseBroadphaseConfirmedBodyCount = this.completedBodyCount;
    this.sparseBroadphaseConfirmedTopologyGeneration = this.completedTopologyGeneration;
    this.sparseBroadphaseConfirmedMutationEpoch = this.completedMutationEpoch;
    if (changed) {
      this.sparseBroadphaseGeneration = (this.sparseBroadphaseGeneration + 1) >>> 0;
    }
  }

  /** Called only after the renderer/native bridge has accepted this frame's submit. */
  confirmSubmittedFrame(frameId: number): void {
    if (this.completedFrameId === (frameId >>> 0)) {
      this.completedFrameSubmitted = true;
      // Submission acceptance can refresh an already-proven allocation after
      // an append/local mutation, but it may never create the first proof.
      this.publishSparseBroadphaseCertificateIfProven();
    }
  }

  getBodyStateAttribute(): StorageBufferAttribute {
    return this.bodyStateAttr;
  }

  getSparseBroadphaseCertificate(
    bodyCount: number,
    topologyGeneration: number | undefined,
  ): SleepingSparseBroadphaseCertificate {
    const boundedBodyCount = Math.max(0, Math.min(this.maxBodies, Math.floor(bodyCount)));
    const normalizedTopologyGeneration = topologyGeneration === undefined
      ? null
      : (topologyGeneration >>> 0);
    const eligible = this.sparseBroadphaseEligible
      && this.completedFrameSubmitted
      && this.mutationEpoch === this.sparseBroadphaseConfirmedMutationEpoch
      && boundedBodyCount === this.sparseBroadphaseConfirmedBodyCount
      && normalizedTopologyGeneration !== null
      && normalizedTopologyGeneration === this.sparseBroadphaseConfirmedTopologyGeneration
      && !this.resetRequested
      && !this.wakeFlushPending
      && this.appendStartBodyCount === null;
    return Object.freeze({
      eligible,
      generation: this.sparseBroadphaseGeneration,
      confirmedFrameId: this.sparseBroadphaseConfirmedFrameId,
      confirmedBodyCount: this.sparseBroadphaseConfirmedBodyCount,
      confirmedTopologyGeneration: this.sparseBroadphaseConfirmedTopologyGeneration,
      confirmedMutationEpoch: this.sparseBroadphaseConfirmedMutationEpoch,
    });
  }

  /** Persistent per-body state that must be transactionally restored on a
   * same-command-buffer contact safety failure. */
  getSafetyTransactionAttributes(): Readonly<{
    bodyState: StorageBufferAttribute;
    stableFrames: StorageBufferAttribute;
    poseHistory: StorageBufferAttribute;
  }> {
    return Object.freeze({
      bodyState: this.bodyStateAttr,
      stableFrames: this.stableFramesAttr,
      poseHistory: this.poseHistoryAttr,
    });
  }

  getTelemetryAttribute(): StorageBufferAttribute {
    return this.telemetryAttr;
  }

  getSolverContactListOffset(): number {
    return this.solverContactCompactionEnabled
      ? this.pairSolverContactsOffset
      : this.pairActiveContactsOffset;
  }

  isSolverContactCompactionEnabled(): boolean {
    return this.solverContactCompactionEnabled;
  }

  /** CPU-only proof surface for regression tests; never maps or submits GPU work. */
  getComputeGroupDiagnostics(): SleepingComputeGroupDiagnostics {
    return Object.freeze({
      beforeSolveGroups: this.beforeSolveComputeGroups.length,
      afterSolveGroups: this.afterSolveComputeGroups.length,
      beforeSolveNodes: this.beforeSolveComputeNodeCount,
      afterSolveNodes: this.afterSolveComputeNodeCount,
      dsuRoundBudget: this.unionPasses,
      dsuCleanupDispatches: this.clearUnionRoundContinuationKernels.length,
    });
  }

  getAllocationTelemetry(): SleepingTopologyCacheAllocationTelemetry {
    return {
      gpuOnly: true,
      totalCpuBytes: this.cachedBodyRootAllocation.cpuPlaceholderByteLength
        + this.cachedPairKeyAllocation.cpuPlaceholderByteLength
        + this.cachePairSeenBitsAllocation.cpuPlaceholderByteLength
        + this.cacheSeenBitsAllocation.cpuPlaceholderByteLength,
      totalGpuBytes: this.cachedBodyRootAllocation.gpuByteLength
        + this.cachedPairKeyAllocation.gpuByteLength
        + this.cachePairSeenBitsAllocation.gpuByteLength
        + this.cacheSeenBitsAllocation.gpuByteLength,
      cachedBodyRootBytes: this.cachedBodyRootAllocation.gpuByteLength,
      cachedPairKeyBytes: this.cachedPairKeyAllocation.gpuByteLength,
      rowSeenBitsetBytes: this.cacheSeenBitsAllocation.gpuByteLength,
      pairSeenBitsetBytes: this.cachePairSeenBitsAllocation.gpuByteLength,
      seenBitsetBytes: this.cacheSeenBitsAllocation.gpuByteLength
        + this.cachePairSeenBitsAllocation.gpuByteLength,
      destroyed: this.cachedBodyRootAllocation.destroyed
        && this.cachedPairKeyAllocation.destroyed
        && this.cachePairSeenBitsAllocation.destroyed
        && this.cacheSeenBitsAllocation.destroyed,
    };
  }

  getLastTelemetry(): SleepingIslandsTelemetry | null {
    return this.lastTelemetry ? { ...this.lastTelemetry } : null;
  }

  telemetryMayCertifyZeroWork(telemetry: SleepingIslandsTelemetry): boolean {
    return !telemetry.failOpen
      && !telemetry.incompatible
      && telemetry.dynamicBodies > 0
      && telemetry.sleepingBodies === telemetry.dynamicBodies
      && telemetry.preSolveSleepingBodies === telemetry.dynamicBodies
      && telemetry.activeContacts > 0
      && telemetry.solverContacts === 0
      && telemetry.solverContactCompaction
      && telemetry.cachedRootHit
      && !telemetry.cachedRootMiss
      && telemetry.cacheValidationFailures === 0
      && telemetry.topologyPath === 'cached-root';
  }

  rejectZeroWorkCertification(): void {
    this.zeroWorkEligible = false;
    this.zeroWorkConfirmedFrameId = -1;
    this.zeroWorkConfirmedBodyCount = 0;
    this.zeroWorkConfirmedTopologyGeneration = null;
    this.zeroWorkConfirmedGravity = null;
  }

  /**
   * Arms the CPU identity-step path only after PhysicsEngine has independently
   * mapped the scene-cumulative contact/CCD/compact and broadphase safety state.
   */
  certifyZeroWork(
    telemetry: SleepingIslandsTelemetry,
    externalSafetyClear: boolean,
  ): boolean {
    const completedGravity = this.completedGravity;
    const exactAllSleepingCertificate = externalSafetyClear
      && this.enabled
      && !this.resetRequested
      && !this.wakeFlushPending
      && this.appendStartBodyCount === null
      && this.completedFrameSubmitted
      && telemetry.frameId === this.completedFrameId
      && this.completedMutationEpoch === this.mutationEpoch
      && this.completedBodyCount > 0
      && this.completedTopologyGeneration !== null
      && completedGravity !== null
      && this.telemetryMayCertifyZeroWork(telemetry);
    this.zeroWorkEligible = exactAllSleepingCertificate;
    if (exactAllSleepingCertificate) {
      this.zeroWorkConfirmedFrameId = telemetry.frameId;
      this.zeroWorkConfirmedBodyCount = this.completedBodyCount;
      this.zeroWorkConfirmedTopologyGeneration = this.completedTopologyGeneration;
      this.zeroWorkConfirmedGravity = [
        completedGravity![0],
        completedGravity![1],
        completedGravity![2],
      ];
      this.zeroWorkConfirmations++;
    } else {
      this.rejectZeroWorkCertification();
    }
    return exactAllSleepingCertificate;
  }

  async readTelemetryAsync(renderer: any): Promise<SleepingIslandsTelemetry | null> {
    if (
      this.disposed
      || !renderer
      || typeof renderer.getArrayBufferAsync !== 'function'
    ) return null;
    const generation = this.readbackGeneration;
    const raw = await renderer.getArrayBufferAsync(this.telemetryAttr) as ArrayBuffer;
    if (this.disposed || generation !== this.readbackGeneration) return null;
    const words = new Uint32Array(raw);
    const floatWord = (index: number): number => {
      const bits = new Uint32Array([words[index] ?? 0]);
      return new Float32Array(bits.buffer)[0] ?? 0;
    };
    const flags = words[TELEMETRY_FLAGS] ?? 0;
    const telemetry: SleepingIslandsTelemetry = {
      frameId: words[TELEMETRY_FRAME] ?? 0,
      flags,
      failOpen: (flags & FRAME_FAIL_MASK) !== 0,
      incompatible: (flags & FRAME_INCOMPATIBLE) !== 0,
      activeContacts: words[TELEMETRY_ACTIVE_CONTACTS] ?? 0,
      dynamicBodies: words[TELEMETRY_DYNAMIC_BODIES] ?? 0,
      islands: words[TELEMETRY_ISLANDS] ?? 0,
      supportedIslands: words[TELEMETRY_SUPPORTED_ISLANDS] ?? 0,
      eligibleIslands: words[TELEMETRY_ELIGIBLE_ISLANDS] ?? 0,
      sleepingBodies: words[TELEMETRY_SLEEPING_BODIES] ?? 0,
      sleepingIslands: words[TELEMETRY_SLEEPING_ISLANDS] ?? 0,
      wakeEvents: words[TELEMETRY_WAKE_EVENTS] ?? 0,
      sleepEvents: words[TELEMETRY_SLEEP_EVENTS] ?? 0,
      invalidInputs: words[TELEMETRY_INVALID_INPUTS] ?? 0,
      unionFailures: words[TELEMETRY_UNION_FAILURES] ?? 0,
      maxIslandSize: words[TELEMETRY_MAX_ISLAND_SIZE] ?? 0,
      maxLinearSpeed: floatWord(TELEMETRY_MAX_LINEAR_SPEED_BITS),
      maxAngularSpeed: floatWord(TELEMETRY_MAX_ANGULAR_SPEED_BITS),
      ineligibleMotionBodies: words[TELEMETRY_INELIGIBLE_MOTION_BODIES] ?? 0,
      ineligibleCorrectionBodies: words[TELEMETRY_INELIGIBLE_CORRECTION_BODIES] ?? 0,
      ineligibleContacts: words[TELEMETRY_INELIGIBLE_CONTACTS] ?? 0,
      maxContactPenetration: floatWord(TELEMETRY_MAX_CONTACT_PENETRATION_BITS),
      fastGated: (words[TELEMETRY_FAST_GATE] ?? 0) !== 0,
      maxIslandDwell: words[TELEMETRY_MAX_ISLAND_DWELL] ?? 0,
      solverContacts: words[TELEMETRY_SOLVER_CONTACTS] ?? 0,
      solverListOverflows: words[TELEMETRY_SOLVER_LIST_OVERFLOWS] ?? 0,
      solverContactCompaction: this.solverContactCompactionEnabled,
      preSolveSleepingBodies: words[TELEMETRY_PRE_SOLVE_SLEEPING_BODIES] ?? 0,
      solverCanonicalAlias: this.solverContactCompactionEnabled
        && (words[TELEMETRY_SOLVER_CANONICAL_ALIAS] ?? 0) !== 0,
      gateMode: (words[TELEMETRY_GATE_MODE] ?? GATE_MODE_AWAKE_FAST)
        === GATE_MODE_FULL_VALIDATE
        ? 'full-validate'
        : 'awake-fast',
      substepEpoch: words[TELEMETRY_SUBSTEP_EPOCH] ?? 0,
      awakeFastGated: (words[TELEMETRY_AWAKE_FAST_GATED] ?? 0) !== 0,
      fullDsuThisSubstep: (words[TELEMETRY_FULL_DSU_THIS_SUBSTEP] ?? 0) !== 0,
      maxCandidateFrames: words[TELEMETRY_MAX_CANDIDATE_FRAMES] ?? 0,
      awakeFastSubsteps: words[TELEMETRY_AWAKE_FAST_SUBSTEPS] ?? 0,
      fullDsuSubsteps: words[TELEMETRY_FULL_DSU_SUBSTEPS] ?? 0,
      candidateFallbacks: words[TELEMETRY_CANDIDATE_FALLBACKS] ?? 0,
      cachedRootHit: (words[TELEMETRY_CACHED_ROOT_HIT] ?? 0) !== 0,
      cachedRootMiss: (words[TELEMETRY_CACHED_ROOT_MISS] ?? 0) !== 0,
      cachedRootHits: words[TELEMETRY_CACHED_ROOT_HITS] ?? 0,
      cachedRootMisses: words[TELEMETRY_CACHED_ROOT_MISSES] ?? 0,
      cacheValidationFailures: words[TELEMETRY_CACHE_VALIDATION_FAILURES] ?? 0,
      topologyPath: (() => {
        switch (words[TELEMETRY_TOPOLOGY_PATH] ?? GATE_PATH_NONE) {
          case GATE_PATH_AWAKE_FAST: return 'awake-fast';
          case GATE_PATH_CACHED_ROOT: return 'cached-root';
          case GATE_PATH_FULL_VALIDATE: return 'full-dsu';
          default: return 'none';
        }
      })(),
      cachedDynamicContacts: words[TELEMETRY_CACHED_DYNAMIC_CONTACTS] ?? 0,
      appendPreserved: (words[TELEMETRY_APPEND_PRESERVED] ?? 0) !== 0,
      compactValidContacts: words[TELEMETRY_COMPACT_VALID_CONTACTS] ?? 0,
      compactSleepingFiltered: words[TELEMETRY_COMPACT_SLEEPING_FILTERED] ?? 0,
      compactInvalidRetained: words[TELEMETRY_COMPACT_INVALID_RETAINED] ?? 0,
      initializedBodies: words[TELEMETRY_INITIALIZED_BODIES] ?? 0,
      dsuRoundsExecuted: words[TELEMETRY_DSU_ROUNDS_EXECUTED] ?? 0,
      dsuDispatchesSaved: words[TELEMETRY_DSU_DISPATCHES_SAVED] ?? 0,
    };
    if (
      !telemetry.failOpen
      && telemetry.invalidInputs === 0
      && telemetry.substepEpoch > 0
      && telemetry.initializedBodies === this.completedBodyCount
      && telemetry.frameId === this.completedFrameId
      && this.completedFrameSubmitted
      && this.completedMutationEpoch === this.mutationEpoch
      && this.completedBodyCount > 0
      && this.completedTopologyGeneration !== null
    ) {
      this.sparseBodyStateGpuPublicationProven = true;
      this.publishSparseBroadphaseCertificateIfProven();
    }
    this.lastTelemetry = telemetry;
    return { ...telemetry };
  }

  dispose(): void {
    this.disposed = true;
    this.readbackGeneration++;
    this.preparedFrameId = -1;
    this.lastTelemetry = null;
    this.zeroWorkEligible = false;
    this.sparseBodyStateGpuPublicationProven = false;
    this.cachePairSeenBitsAllocation.destroy();
    this.cacheSeenBitsAllocation.destroy();
    this.cachedPairKeyAllocation.destroy();
    this.cachedBodyRootAllocation.destroy();
  }
}
